import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Plus,
  Trash2,
  Lock,
  Trophy,
  UserCheck,
  X,
  Users,
  Share2,
  Copy,
  Check,
  CheckCircle2,
  RotateCcw,
  Tv,
  Printer,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../config/firebase";
import { Header } from "../../components/layout/Header";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { Input } from "../../components/common/Input";
import { LoadingSpinner } from "../../components/common/LoadingSpinner";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../services/api";
import {
  PariMutuelEvent,
  PariMutuelBet,
  PariMutuelPayoutRow,
  PariMutuelParticipant,
} from "../../types";
import { COLORS } from "../../config/colors";

// ── Helpers ────────────────────────────────────────────────────────────────

function sortParticipants(
  participants: PariMutuelParticipant[],
): PariMutuelParticipant[] {
  return [...participants].sort((a, b) => {
    const na = parseInt(a.name, 10);
    const nb = parseInt(b.name, 10);
    if (!isNaN(na) && !isNaN(nb)) return na - nb;
    if (!isNaN(na)) return -1;
    if (!isNaN(nb)) return 1;
    return a.name.localeCompare(b.name);
  });
}

type TabId = "settings" | "bets" | "results";

const TAB_LABELS: Record<TabId, string> = {
  settings: "Settings",
  bets: "Bets",
  results: "Results",
};

// ── Cast / Display View Overlay ────────────────────────────────────────────

function CastViewOverlay({
  pm,
  onClose,
}: {
  pm: PariMutuelEvent;
  onClose: () => void;
}) {
  const scratchedIds = new Set(
    (pm.participants ?? []).filter((p) => p.scratched).map((p) => p.id),
  );
  const participantById = new Map(
    (pm.participants ?? []).map((p) => [p.id, p]),
  );

  // When locked or settled, show only confirmed (paid) bets
  const showOnlyPaid = pm.status === "locked" || pm.status === "settled";
  const betsToShow = pm.bets.filter(
    (b) => !b.voided && (!showOnlyPaid || b.paid !== false),
  );

  const byBettor = new Map<string, PariMutuelBet[]>();
  for (const bet of betsToShow) {
    const arr = byBettor.get(bet.bettorName) ?? [];
    arr.push(bet);
    byBettor.set(bet.bettorName, arr);
  }

  const pendingBets = pm.bets.filter(
    (b) => !b.voided && b.paid === false && !scratchedIds.has(b.participantId),
  );
  const pendingByBettor = new Map<string, PariMutuelBet[]>();
  for (const bet of pendingBets) {
    const arr = pendingByBettor.get(bet.bettorName) ?? [];
    arr.push(bet);
    pendingByBettor.set(bet.bettorName, arr);
  }

  const scratchedPaidBets = pm.bets.filter(
    (b) => !b.voided && b.paid === true && scratchedIds.has(b.participantId),
  );
  const refundByBettor = new Map<string, PariMutuelBet[]>();
  for (const bet of scratchedPaidBets) {
    const arr = refundByBettor.get(bet.bettorName) ?? [];
    arr.push(bet);
    refundByBettor.set(bet.bettorName, arr);
  }

  const activeBets = pm.bets.filter(
    (b) => !scratchedIds.has(b.participantId) && !b.voided,
  );
  const winPool = activeBets.reduce((s, b) => s + b.winAmount, 0);
  const placePool = activeBets.reduce((s, b) => s + b.placeAmount, 0);
  const showPool = activeBets.reduce((s, b) => s + b.showAmount, 0);
  const totalPool = pm.bets
    .filter((b) => !b.voided)
    .reduce((s, b) => s + b.winAmount + b.placeAmount + b.showAmount, 0);

  // Auto-scroll the left panel
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let raf: number;
    let pauseUntil = 0;
    let atBottom = false;
    function step(ts: number) {
      if (!el) return;
      // During pause: hold position (don't scroll)
      if (ts < pauseUntil) {
        raf = requestAnimationFrame(step);
        return;
      }
      // Pause just expired and we were sitting at the bottom — now jump to top
      if (atBottom) {
        atBottom = false;
        el.scrollTop = 0;
        raf = requestAnimationFrame(step);
        return;
      }
      const max = el.scrollHeight - el.clientHeight;
      if (max <= 0) {
        raf = requestAnimationFrame(step);
        return;
      }
      if (el.scrollTop >= max - 1) {
        // Reached the bottom — pause here before looping
        atBottom = true;
        pauseUntil = ts + 7000; // 7 s pause at bottom
      } else {
        el.scrollTop += 0.5; // ~30 px/s at 60 fps
      }
      raf = requestAnimationFrame(step);
    }
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  const statusLabel =
    pm.status === "open"
      ? "🟢 Betting Open"
      : pm.status === "locked"
        ? "🔒 Betting Locked"
        : pm.status === "pending"
          ? "⏳ Not Yet Open"
          : "✅ Settled";

  const statusColor =
    pm.status === "settled"
      ? "bg-green-600"
      : pm.status === "locked"
        ? "bg-amber-500"
        : pm.status === "pending"
          ? "bg-sky-600"
          : "bg-emerald-500";

  // Payout totals by bettor (used for settled view)
  const payoutByBettor: [
    string,
    { win: number; place: number; show: number; total: number },
  ][] = pm.payouts
    ? (() => {
        const map = new Map<
          string,
          { win: number; place: number; show: number; total: number }
        >();
        for (const row of pm.payouts.payoutRows) {
          if (row.scratched) continue;
          const cur = map.get(row.bettorName) ?? {
            win: 0,
            place: 0,
            show: 0,
            total: 0,
          };
          cur.win += row.winPayout;
          cur.place += row.placePayout;
          cur.show += row.showPayout;
          cur.total += row.totalPayout;
          map.set(row.bettorName, cur);
        }
        for (const w of pm.reconciliation?.winners ?? []) {
          const cur = map.get(w.bettorName) ?? {
            win: 0,
            place: 0,
            show: 0,
            total: 0,
          };
          cur.total += w.amount;
          map.set(w.bettorName, cur);
        }
        return Array.from(map.entries())
          .filter(([, t]) => t.total > 0)
          .sort((a, b) => b[1].total - a[1].total);
      })()
    : [];

  // Helper: shared finishing-order block
  const finishingOrder = pm.parameters ? (
    <div className='bg-white/5 rounded-2xl border border-white/10 overflow-hidden shrink-0'>
      <div className='px-4 py-2 bg-white/10 border-b border-white/10'>
        <p className='text-xs font-bold text-white/50 uppercase tracking-widest'>
          Final Finishing Order
        </p>
      </div>
      <div className='flex divide-x divide-white/10'>
        {[
          { label: "🥇 Win", id: pm.parameters.winParticipantId },
          { label: "🥈 Place", id: pm.parameters.placeParticipantId },
          { label: "🥉 Show", id: pm.parameters.showParticipantId },
        ].map(({ label, id }) => (
          <div key={label} className='flex-1 px-4 py-3 text-center'>
            <p className='text-xs text-white/40 mb-1'>{label}</p>
            <p className='font-bold text-white text-base'>
              {participantById.get(id)?.name ?? id}
            </p>
          </div>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <div className='fixed inset-0 z-50 bg-[#0a1a27] flex flex-col'>
      {/* ── Header ── */}
      <div className='bg-[#102232] px-6 py-3 flex items-center gap-3 border-b border-white/10 shrink-0'>
        <img
          src='/logo.png'
          alt='Golf Kraken'
          className='h-8 w-8 rounded-md object-contain'
        />
        <span className='text-xs font-bold text-white/50 uppercase tracking-widest'>
          Golf Kraken
        </span>
        <span className='text-xl font-bold text-white mx-2'>
          {pm.eventName}
        </span>
        <span
          className={`text-xs font-semibold px-3 py-1 rounded-full text-white ${statusColor}`}
        >
          {statusLabel}
        </span>
        {showOnlyPaid && (
          <span className='text-xs text-white/40 font-medium ml-1'>
            · official bets only
          </span>
        )}
        <button
          onClick={onClose}
          className='ml-auto p-2 rounded-lg hover:bg-white/10 transition-colors'
          aria-label='Close display view'
        >
          <X size={22} className='text-white/60' />
        </button>
      </div>

      {/* ── Body ── */}
      <div className='flex flex-1 overflow-hidden'>
        {/* ── LEFT: Bets + Pending + Refunds (auto-scrolling) ── */}
        <div
          ref={scrollRef}
          className='w-1/2 overflow-y-auto border-r border-white/10 px-5 py-4 space-y-5'
          style={{ scrollbarWidth: "none" } as React.CSSProperties}
        >
          <div className='flex items-center gap-2'>
            <span className='text-xs font-bold text-white/40 uppercase tracking-widest'>
              Bets
            </span>
            <span className='text-xs text-white/30'>
              · {betsToShow.length} shown
              {showOnlyPaid ? " (confirmed only)" : ""}
            </span>
          </div>

          {betsToShow.length === 0 ? (
            <p className='text-sm text-white/30 text-center py-8'>
              {showOnlyPaid ? "No confirmed bets yet." : "No bets yet."}
            </p>
          ) : (
            <div className='space-y-3'>
              {Array.from(byBettor.entries()).map(([name, bets]) => {
                const total = bets.reduce(
                  (s, b) => s + b.winAmount + b.placeAmount + b.showAmount,
                  0,
                );
                return (
                  <div
                    key={name}
                    className='bg-white/5 rounded-2xl border border-white/10 overflow-hidden'
                  >
                    <div className='flex items-center justify-between px-4 py-2 bg-white/5 border-b border-white/10'>
                      <span className='font-bold text-white text-sm'>
                        {name}
                      </span>
                      <span className='text-xs text-white/40'>
                        {bets.length} bet{bets.length !== 1 ? "s" : ""} · $
                        {total.toFixed(2)}
                      </span>
                    </div>
                    <table className='w-full text-xs'>
                      <tbody>
                        {bets.map((bet) => {
                          const isScratched = scratchedIds.has(
                            bet.participantId,
                          );
                          return (
                            <tr
                              key={bet.id}
                              className={`border-b border-white/5 last:border-0 ${isScratched ? "opacity-40" : ""}`}
                            >
                              <td className='px-4 py-2 text-white/80 font-medium'>
                                <span
                                  className={isScratched ? "line-through" : ""}
                                >
                                  {bet.participantName ??
                                    `#${bet.horseNumber}${bet.horseName ? ` ${bet.horseName}` : ""}`}
                                </span>
                                {isScratched && (
                                  <span className='ml-1.5 text-[10px] bg-orange-500/20 text-orange-400 rounded px-1'>
                                    scratched
                                  </span>
                                )}
                                {!isScratched && bet.paid === false && (
                                  <span className='ml-1.5 text-[10px] bg-amber-500/20 text-amber-400 rounded px-1'>
                                    pending
                                  </span>
                                )}
                              </td>
                              <td className='px-2 py-2 text-right text-white/50'>
                                {bet.winAmount > 0 ? `$${bet.winAmount}` : "—"}
                              </td>
                              <td className='px-2 py-2 text-right text-white/50'>
                                {bet.placeAmount > 0
                                  ? `$${bet.placeAmount}`
                                  : "—"}
                              </td>
                              <td className='px-3 py-2 text-right text-white/50'>
                                {bet.showAmount > 0
                                  ? `$${bet.showAmount}`
                                  : "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Pending Payments (read-only) ── */}
          {pendingByBettor.size > 0 && (
            <div>
              <div className='flex items-center gap-2 bg-amber-500 rounded-t-2xl px-4 py-2'>
                <span className='text-sm font-bold text-white tracking-wide'>
                  ⏳ PENDING PAYMENTS
                </span>
                <span className='ml-auto text-xs font-semibold bg-white/25 text-white rounded-full px-2 py-0.5'>
                  {pendingBets.length} bet{pendingBets.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div className='bg-amber-900/20 border border-amber-500/30 border-t-0 rounded-b-2xl overflow-hidden'>
                <table className='w-full text-sm'>
                  <thead>
                    <tr className='border-b border-amber-500/20 bg-amber-500/10'>
                      <th className='text-left px-4 py-2 text-xs font-semibold text-amber-300'>
                        Bettor
                      </th>
                      <th className='text-left px-2 py-2 text-xs font-semibold text-amber-300'>
                        Horse
                      </th>
                      <th className='text-right px-3 py-2 text-xs font-semibold text-amber-300'>
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from(pendingByBettor.entries()).map(
                      ([bettorName, bettorBets], groupIdx) => {
                        const isLastGroup =
                          groupIdx === pendingByBettor.size - 1;
                        const bettorTotal = bettorBets.reduce(
                          (s, b) =>
                            s + b.winAmount + b.placeAmount + b.showAmount,
                          0,
                        );
                        return [
                          ...bettorBets.map((bet, i) => (
                            <tr
                              key={bet.id}
                              className='border-b border-amber-500/10'
                            >
                              <td className='px-4 py-2 text-xs font-semibold text-amber-100'>
                                {i === 0 ? bettorName : ""}
                              </td>
                              <td className='px-2 py-2 text-xs text-white/60'>
                                {bet.participantName}
                              </td>
                              <td className='px-3 py-2 text-xs text-right font-semibold text-amber-300'>
                                $
                                {bet.winAmount +
                                  bet.placeAmount +
                                  bet.showAmount}
                              </td>
                            </tr>
                          )),
                          <tr
                            key={`${bettorName}-sub`}
                            className={[
                              "border-b border-amber-500/10",
                              !isLastGroup
                                ? "border-b-2 border-b-amber-500/40"
                                : "",
                            ].join(" ")}
                          >
                            <td className='px-4 py-2' />
                            <td className='px-2 py-2 text-xs font-semibold text-white/40 italic'>
                              Total Owed
                            </td>
                            <td className='px-3 py-2 text-xs font-bold text-amber-400 text-right'>
                              ${bettorTotal}
                            </td>
                          </tr>,
                        ];
                      },
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Scratched Refunds (read-only) ── */}
          {refundByBettor.size > 0 && (
            <div>
              <div className='flex items-center gap-2 bg-red-500 rounded-t-2xl px-4 py-2'>
                <span className='text-sm font-bold text-white tracking-wide'>
                  💸 SCRATCHED BET REFUNDS
                </span>
                <span className='ml-auto text-xs font-semibold bg-white/25 text-white rounded-full px-2 py-0.5'>
                  {scratchedPaidBets.length} bet
                  {scratchedPaidBets.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div className='bg-red-900/20 border border-red-500/30 border-t-0 rounded-b-2xl overflow-hidden'>
                <table className='w-full text-sm'>
                  <thead>
                    <tr className='border-b border-red-500/20 bg-red-500/10'>
                      <th className='text-left px-4 py-2 text-xs font-semibold text-red-300'>
                        Bettor
                      </th>
                      <th className='text-left px-2 py-2 text-xs font-semibold text-red-300'>
                        Horse
                      </th>
                      <th className='text-right px-3 py-2 text-xs font-semibold text-red-300'>
                        Refund
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from(refundByBettor.entries()).map(
                      ([bettorName, bettorBets]) => {
                        const total = bettorBets.reduce(
                          (s, b) =>
                            s + b.winAmount + b.placeAmount + b.showAmount,
                          0,
                        );
                        return bettorBets.map((bet, i) => (
                          <tr
                            key={bet.id}
                            className='border-b border-red-500/10 last:border-0'
                          >
                            <td className='px-4 py-2 text-xs font-semibold text-red-100'>
                              {i === 0 ? bettorName : ""}
                            </td>
                            <td className='px-2 py-2 text-xs text-white/60 line-through'>
                              {bet.participantName}
                            </td>
                            <td className='px-3 py-2 text-xs text-right font-bold text-red-400'>
                              {i === bettorBets.length - 1 ? `$${total}` : ""}
                            </td>
                          </tr>
                        ));
                      },
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* ── RIGHT: Results (no scroll) ── */}
        <div className='w-1/2 overflow-hidden flex flex-col px-5 py-4 gap-4'>
          {/* Pool totals — always shown */}
          <div className='grid grid-cols-4 gap-2 shrink-0'>
            {[
              { label: "Win Pool", v: winPool, highlight: false },
              { label: "Place Pool", v: placePool, highlight: false },
              { label: "Show Pool", v: showPool, highlight: false },
              { label: "Total Purse", v: totalPool, highlight: true },
            ].map(({ label, v, highlight }) => (
              <div
                key={label}
                className={[
                  "rounded-xl p-3 border",
                  highlight
                    ? "bg-green-900/40 border-green-500/30"
                    : "bg-white/5 border-white/10",
                ].join(" ")}
              >
                <p
                  className={`text-[11px] ${highlight ? "text-green-400" : "text-white/40"}`}
                >
                  {label}
                </p>
                <p
                  className={`font-bold text-lg ${highlight ? "text-green-300" : "text-white/80"}`}
                >
                  ${v.toFixed(2)}
                </p>
              </div>
            ))}
          </div>

          {/* State-dependent results content */}
          {pm.status === "settled" && pm.parameters && pm.payouts ? (
            // ── Settled: finishing order + payouts ──
            <div className='flex-1 flex flex-col gap-3 overflow-hidden'>
              {finishingOrder}
              {payoutByBettor.length > 0 && (
                <div className='flex-1 overflow-hidden bg-white/5 rounded-2xl border border-white/10 flex flex-col'>
                  <div className='px-4 py-2 bg-green-700/40 border-b border-white/10 shrink-0'>
                    <p className='text-xs font-bold text-green-300 uppercase tracking-widest'>
                      🏆 Payouts
                    </p>
                  </div>
                  <table className='w-full text-sm'>
                    <thead className='bg-white/5'>
                      <tr>
                        <th className='text-left px-4 py-2 text-xs text-white/40 font-semibold'>
                          Name
                        </th>
                        <th className='text-right px-2 py-2 text-xs text-white/40 font-semibold'>
                          Win
                        </th>
                        <th className='text-right px-2 py-2 text-xs text-white/40 font-semibold'>
                          Place
                        </th>
                        <th className='text-right px-2 py-2 text-xs text-white/40 font-semibold'>
                          Show
                        </th>
                        <th className='text-right px-4 py-2 text-xs text-white/40 font-semibold'>
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {payoutByBettor.map(([name, totals]) => (
                        <tr
                          key={name}
                          className='border-b border-white/5 last:border-0'
                        >
                          <td className='px-4 py-2.5 font-semibold text-white'>
                            {name}
                          </td>
                          <td className='px-2 py-2.5 text-right text-white/50'>
                            {totals.win > 0 ? `$${totals.win.toFixed(2)}` : "—"}
                          </td>
                          <td className='px-2 py-2.5 text-right text-white/50'>
                            {totals.place > 0
                              ? `$${totals.place.toFixed(2)}`
                              : "—"}
                          </td>
                          <td className='px-2 py-2.5 text-right text-white/50'>
                            {totals.show > 0
                              ? `$${totals.show.toFixed(2)}`
                              : "—"}
                          </td>
                          <td className='px-4 py-2.5 text-right font-bold text-green-400'>
                            ${totals.total.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : pm.status === "locked" && pm.parameters ? (
            // ── Locked + results entered, payouts pending ──
            <div className='flex-1 flex flex-col gap-3'>
              {finishingOrder}
              <div className='flex-1 flex flex-col items-center justify-center gap-2 text-center'>
                <span className='text-3xl'>⏳</span>
                <p className='text-base font-semibold text-white/50'>
                  Awaiting Payout Calculation
                </p>
              </div>
            </div>
          ) : (
            // ── Not ready yet ──
            <div className='flex-1 flex flex-col items-center justify-center gap-3 text-center'>
              {pm.status === "pending" && (
                <>
                  <span className='text-5xl'>⏳</span>
                  <p className='text-xl font-semibold text-white/50'>
                    Betting Not Yet Open
                  </p>
                  <p className='text-sm text-white/25'>
                    Results will appear here once the event is settled.
                  </p>
                </>
              )}
              {pm.status === "open" && (
                <>
                  <span className='text-5xl'>🟢</span>
                  <p className='text-xl font-semibold text-white/50'>
                    Betting is Open
                  </p>
                  <p className='text-sm text-white/25'>
                    Results will appear here once the event is settled.
                  </p>
                </>
              )}
              {pm.status === "locked" && (
                <>
                  <span className='text-5xl'>🔒</span>
                  <p className='text-xl font-semibold text-white/50'>
                    Betting Locked
                  </p>
                  <p className='text-sm text-white/25'>Awaiting results...</p>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function PariMutuelPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { user } = useAuth();
  const [pm, setPm] = useState<PariMutuelEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>("settings");

  // ── Settings tab state ─────────────────────────────────────────────────────
  const [minBetInput, setMinBetInput] = useState("");
  const [maxBetInput, setMaxBetInput] = useState("");
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState("");
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [copiedShareLink, setCopiedShareLink] = useState(false);

  // ── Bets tab state ─────────────────────────────────────────────────────────
  const [markPaidLoading, setMarkPaidLoading] = useState<string | null>(null);

  // ── Field tab state ──────────────────────────────────────────────────────
  const [newParticipantName, setNewParticipantName] = useState("");
  const [addParticipantLoading, setAddParticipantLoading] = useState(false);
  const [addParticipantError, setAddParticipantError] = useState("");

  // ── Bets tab state ───────────────────────────────────────────────────────
  const [activeBettor, setActiveBettor] = useState<string | null>(null);
  const [nameInput, setNameInput] = useState("");
  const [nameDropdownOpen, setNameDropdownOpen] = useState(false);
  const nameRef = useRef<HTMLDivElement>(null);
  const pendingPaymentsRef = useRef<HTMLDivElement>(null);
  const prevBetCountRef = useRef<number | null>(null);
  const prevBetsRef = useRef<PariMutuelBet[]>([]);
  const [newBetFlash, setNewBetFlash] = useState(0);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [castViewOpen, setCastViewOpen] = useState(false);

  // ── Toast state ────────────────────────────────────────────────────────────
  interface Toast {
    id: number;
    bettorName: string;
    total: number;
  }
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastIdRef = useRef(0);

  function addToast(bettorName: string, total: number) {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, bettorName, total }]);
    setTimeout(
      () => setToasts((prev) => prev.filter((t) => t.id !== id)),
      5000,
    );
  }

  const [selectedParticipantId, setSelectedParticipantId] = useState("");
  const [winAmt, setWinAmt] = useState("");
  const [placeAmt, setPlaceAmt] = useState("");
  const [showAmt, setShowAmt] = useState("");
  const [betPaid, setBetPaid] = useState(true);
  const [betError, setBetError] = useState("");
  const [betLoading, setBetLoading] = useState(false);

  const [filterName, setFilterName] = useState("");
  const [filterBetType, setFilterBetType] = useState<
    "all" | "win" | "place" | "show"
  >("all");
  const [filterParticipantId, setFilterParticipantId] = useState("");

  // ── Results tab state ────────────────────────────────────────────────────
  const [winParticipantId, setWinParticipantId] = useState("");
  const [placeParticipantId, setPlaceParticipantId] = useState("");
  const [showParticipantId, setShowParticipantId] = useState("");
  const [paramError, setParamError] = useState("");
  const [paramLoading, setParamLoading] = useState(false);
  const [editingParams, setEditingParams] = useState(false);
  const [showFullBreakdown, setShowFullBreakdown] = useState(false);
  const [expandedWinners, setExpandedWinners] = useState<Set<string>>(
    new Set(),
  );
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [reconcileError, setReconcileError] = useState("");
  const [drawCount, setDrawCount] = useState(2);

  const isOwner = user?.uid === pm?.createdBy;

  const sortedParticipants = useMemo(
    () => sortParticipants(pm?.participants ?? []),
    [pm?.participants],
  );

  const existingBettorNames = useMemo(() => {
    if (!pm) return [];
    return Array.from(new Set(pm.bets.map((b) => b.bettorName))).sort((a, b) =>
      a.localeCompare(b),
    );
  }, [pm]);

  const filteredBettorNames = useMemo(() => {
    if (!nameInput.trim()) return existingBettorNames;
    const q = nameInput.toLowerCase();
    return existingBettorNames.filter((n) => n.toLowerCase().includes(q));
  }, [nameInput, existingBettorNames]);

  const betsByBettor = useMemo(() => {
    if (!pm) return new Map<string, PariMutuelBet[]>();
    const map = new Map<string, PariMutuelBet[]>();
    for (const bet of pm.bets) {
      const arr = map.get(bet.bettorName) ?? [];
      arr.push(bet);
      map.set(bet.bettorName, arr);
    }
    return map;
  }, [pm]);

  const filteredBetsByBettor = useMemo(() => {
    const result = new Map<string, PariMutuelBet[]>();
    for (const [name, bets] of betsByBettor.entries()) {
      if (
        filterName.trim() &&
        !name.toLowerCase().includes(filterName.toLowerCase())
      )
        continue;
      const matched = bets.filter((b) => {
        if (filterBetType === "win" && b.winAmount <= 0) return false;
        if (filterBetType === "place" && b.placeAmount <= 0) return false;
        if (filterBetType === "show" && b.showAmount <= 0) return false;
        if (filterParticipantId && b.participantId !== filterParticipantId)
          return false;
        return true;
      });
      if (matched.length > 0) result.set(name, matched);
    }
    return result;
  }, [betsByBettor, filterName, filterBetType, filterParticipantId]);

  const filtersActive =
    filterName.trim() !== "" ||
    filterBetType !== "all" ||
    filterParticipantId !== "";

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (nameRef.current && !nameRef.current.contains(e.target as Node)) {
        setNameDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!eventId) return;
    loadPm();
  }, [eventId]);

  // Real-time Firestore listener — keeps bets up-to-date as guests submit
  useEffect(() => {
    if (!pm?.id) return;
    const unsub = onSnapshot(
      doc(db, "pariMutuelEvents", pm.id),
      (snap) => {
        if (!snap.exists()) return;
        const updated = {
          id: snap.id,
          ...snap.data(),
        } as unknown as PariMutuelEvent;
        const newCount = updated.bets?.length ?? 0;
        if (
          prevBetCountRef.current !== null &&
          newCount > prevBetCountRef.current
        ) {
          const diff = newCount - prevBetCountRef.current;
          setNewBetFlash(diff);
          setTimeout(() => setNewBetFlash(0), 6000);
          // Toast for each new bet
          const prevIds = new Set(prevBetsRef.current.map((b) => b.id));
          const newBets = (updated.bets ?? []).filter(
            (b) => !prevIds.has(b.id),
          );
          for (const bet of newBets) {
            const total = bet.winAmount + bet.placeAmount + bet.showAmount;
            addToast(bet.bettorName, total);
          }
        }
        prevBetCountRef.current = newCount;
        prevBetsRef.current = updated.bets ?? [];
        setPm(updated);
      },
      (err) => console.error("Firestore listener error:", err),
    );
    return unsub;
  }, [pm?.id]);

  useEffect(() => {
    if (pm) {
      setMinBetInput(pm.minBet != null ? String(pm.minBet) : "");
      setMaxBetInput(pm.maxBet != null ? String(pm.maxBet) : "");
    }
  }, [pm?.minBet, pm?.maxBet]);

  useEffect(() => {
    if (pm?.parameters) {
      setWinParticipantId(pm.parameters.winParticipantId ?? "");
      setPlaceParticipantId(pm.parameters.placeParticipantId ?? "");
      setShowParticipantId(pm.parameters.showParticipantId ?? "");
    }
  }, [pm?.parameters]);

  async function loadPm() {
    try {
      const res = (await api.pariMutuel.getByEventId(eventId!)) as {
        data?: PariMutuelEvent;
      };
      setPm(res.data ?? null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  // ── Settings tab handlers ────────────────────────────────────────────────

  async function handleUpdateSettings(e: React.FormEvent) {
    e.preventDefault();
    if (!pm) return;
    setSettingsError("");
    setSettingsSaved(false);
    setSettingsLoading(true);
    try {
      const minBet = minBetInput !== "" ? Number(minBetInput) : null;
      const maxBet = maxBetInput !== "" ? Number(maxBetInput) : null;
      await api.pariMutuel.updateSettings(pm.id, { minBet, maxBet });
      setSettingsSaved(true);
      await loadPm();
    } catch (err: any) {
      setSettingsError(err.message ?? "Failed to update settings");
    } finally {
      setSettingsLoading(false);
    }
  }

  async function handleToggleShowAllBets() {
    if (!pm) return;
    try {
      await api.pariMutuel.updateSettings(pm.id, {
        showAllBets: !pm.showAllBets,
      });
      await loadPm();
    } catch (err: any) {
      alert(err.message ?? "Failed to update setting");
    }
  }

  function handleCopyShareLink() {
    if (!pm?.shareCode) return;
    const url = `${window.location.origin}/join/${pm.shareCode}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedShareLink(true);
      setTimeout(() => setCopiedShareLink(false), 2000);
    });
  }

  function handlePrintShare() {
    window.print();
  }

  async function handleMarkPaid(betId: string, paid: boolean) {
    if (!pm) return;
    setMarkPaidLoading(betId);
    try {
      await api.pariMutuel.markBetPaid(pm.id, betId, paid);
      await loadPm();
    } finally {
      setMarkPaidLoading(null);
    }
  }

  // ── Field tab handlers ───────────────────────────────────────────────────

  async function handleAddParticipant(e: React.FormEvent) {
    e.preventDefault();
    if (!pm) return;
    setAddParticipantError("");
    setAddParticipantLoading(true);
    try {
      await api.pariMutuel.addParticipant(pm.id, {
        name: newParticipantName.trim(),
      });
      setNewParticipantName("");
      await loadPm();
    } catch (err: any) {
      setAddParticipantError(err.message ?? "Failed to add participant");
    } finally {
      setAddParticipantLoading(false);
    }
  }

  async function handleRemoveParticipant(participantId: string) {
    if (!pm) return;
    try {
      await api.pariMutuel.removeParticipant(pm.id, participantId);
      await loadPm();
    } catch (err: any) {
      alert(err.message ?? "Failed to remove participant");
    }
  }

  async function handleScratch(participantId: string, scratched: boolean) {
    if (!pm) return;
    try {
      await api.pariMutuel.scratchParticipant(pm.id, participantId, scratched);
      await loadPm();
    } catch (err: any) {
      alert(err.message ?? "Failed to update scratch status");
    }
  }

  // ── Bet handlers ─────────────────────────────────────────────────────────

  function selectBettor(name: string) {
    setActiveBettor(name);
    setNameInput("");
    setNameDropdownOpen(false);
    setSelectedParticipantId("");
    setWinAmt("");
    setPlaceAmt("");
    setShowAmt("");
    setBetError("");
  }

  function clearBettor() {
    setActiveBettor(null);
    setNameInput("");
  }

  async function handleAddBet(e: React.FormEvent) {
    e.preventDefault();
    if (!pm || !activeBettor) return;
    setBetError("");
    setBetLoading(true);
    try {
      const win = Number(winAmt) || 0;
      const place = Number(placeAmt) || 0;
      const show = Number(showAmt) || 0;
      const amounts = [win, place, show].filter((a) => a > 0);
      const { minBet, maxBet } = pm;
      if (minBet != null) {
        const below = amounts.find((a) => a < minBet);
        if (below != null) {
          setBetError(`Minimum bet is $${minBet}`);
          setBetLoading(false);
          return;
        }
      }
      if (maxBet != null) {
        const above = amounts.find((a) => a > maxBet);
        if (above != null) {
          setBetError(`Maximum bet is $${maxBet}`);
          setBetLoading(false);
          return;
        }
      }
      await api.pariMutuel.addBet(pm.id, {
        bettorName: activeBettor,
        participantId: selectedParticipantId,
        winAmount: win,
        placeAmount: place,
        showAmount: show,
        paid: betPaid,
      });
      setSelectedParticipantId("");
      setWinAmt("");
      setPlaceAmt("");
      setShowAmt("");
      setBetPaid(true);
      await loadPm();
    } catch (err: any) {
      setBetError(err.message ?? "Failed to add bet");
    } finally {
      setBetLoading(false);
    }
  }

  async function handleRemoveBet(betId: string) {
    if (!pm) return;
    await api.pariMutuel.removeBet(pm.id, betId);
    await loadPm();
  }

  // ── Results handlers ─────────────────────────────────────────────────────

  async function handleSaveResults(e: React.FormEvent) {
    e.preventDefault();
    if (!pm) return;
    setParamError("");
    setParamLoading(true);
    try {
      await api.pariMutuel.saveResults(pm.id, {
        winParticipantId,
        placeParticipantId,
        showParticipantId,
      });
      setEditingParams(false);
      await loadPm();
    } catch (err: any) {
      setParamError(err.message ?? "Failed to save results");
    } finally {
      setParamLoading(false);
    }
  }

  async function handleCalculatePayouts() {
    if (!pm?.parameters) return;
    setParamError("");
    setParamLoading(true);
    try {
      await api.pariMutuel.settle(pm.id, {
        winParticipantId: pm.parameters.winParticipantId,
        placeParticipantId: pm.parameters.placeParticipantId,
        showParticipantId: pm.parameters.showParticipantId,
      });
      await loadPm();
    } catch (err: any) {
      setParamError(err.message ?? "Failed to calculate payouts");
    } finally {
      setParamLoading(false);
    }
  }

  async function handleReconcile() {
    if (!pm) return;
    setReconcileError("");
    setReconcileLoading(true);
    try {
      await api.pariMutuel.reconcile(pm.id, drawCount);
      await loadPm();
    } catch (err: any) {
      setReconcileError(err.message ?? "Failed to reconcile");
    } finally {
      setReconcileLoading(false);
    }
  }

  async function handleSettle(e: React.FormEvent) {
    e.preventDefault();
    if (!pm) return;
    setParamError("");
    setParamLoading(true);
    try {
      await api.pariMutuel.settle(pm.id, {
        winParticipantId,
        placeParticipantId,
        showParticipantId,
      });
      setEditingParams(false);
      await loadPm();
    } catch (err: any) {
      setParamError(err.message ?? "Failed to calculate payouts");
    } finally {
      setParamLoading(false);
    }
  }

  async function handleLock() {
    if (!pm) return;
    await api.pariMutuel.lock(pm.id);
    await loadPm();
  }

  async function handleOpen() {
    if (!pm) return;
    await api.pariMutuel.open(pm.id);
    await loadPm();
  }

  async function handleClose() {
    if (!pm) return;
    await api.pariMutuel.close(pm.id);
    await loadPm();
  }

  async function handleUnlock() {
    if (!pm) return;
    await api.pariMutuel.unlock(pm.id);
    await loadPm();
  }

  async function handleMarkSettled() {
    if (!pm) return;
    await api.pariMutuel.markSettled(pm.id);
    await loadPm();
  }

  async function handleUnsettle() {
    if (!pm) return;
    await api.pariMutuel.unsettle(pm.id);
    await loadPm();
  }

  // ── Render ───────────────────────────────────────────────────────────────

  if (loading) return <LoadingSpinner fullScreen />;

  if (!pm) {
    return (
      <>
        <Header title='Parimutuel Betting' showBack />
        <div className='px-4 py-12 text-center'>
          <p className='text-gray-500'>Parimutuel event not found.</p>
        </div>
      </>
    );
  }

  const scratchedParticipantIds = new Set(
    (pm.participants ?? []).filter((p) => p.scratched).map((p) => p.id),
  );
  const activeBets = pm.bets.filter(
    (b) => !scratchedParticipantIds.has(b.participantId) && !b.voided,
  );
  // totalPool counts only bets that are paid and not voided
  const totalPool = pm.bets
    .filter((b) => !b.voided)
    .reduce((s, b) => s + b.winAmount + b.placeAmount + b.showAmount, 0);
  const winPool = activeBets.reduce((s, b) => s + b.winAmount, 0);
  const placePool = activeBets.reduce((s, b) => s + b.placeAmount, 0);
  const showPool = activeBets.reduce((s, b) => s + b.showAmount, 0);

  const participantById = new Map(
    (pm.participants ?? []).map((p) => [p.id, p]),
  );

  return (
    <>
      {/* ── Toast notifications ── */}
      <div className='fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none'>
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className='pointer-events-auto flex items-center gap-3 bg-primary-800 text-white rounded-2xl shadow-xl px-4 py-3 min-w-[220px] max-w-xs animate-in slide-in-from-right fade-in duration-300'
          >
            <span className='text-lg'>🎲</span>
            <div className='flex-1 min-w-0'>
              <p className='text-xs font-bold truncate'>{toast.bettorName}</p>
              <p className='text-xs text-white/70'>
                New bet · ${toast.total.toFixed(2)}
              </p>
            </div>
            <button
              onClick={() =>
                setToasts((prev) => prev.filter((t) => t.id !== toast.id))
              }
              className='text-white/50 hover:text-white p-0.5'
            >
              <X size={12} />
            </button>
          </div>
        ))}
      </div>

      {/* ── Print-only betting slip ── */}
      <style>{`
        @media screen {
          #betting-print-sheet { visibility: hidden; position: fixed; height: 0; overflow: hidden; pointer-events: none; }
        }
        @media print {
          body * { visibility: hidden; }
          #betting-print-sheet, #betting-print-sheet * { visibility: visible; }
          #betting-print-sheet {
            position: fixed !important; inset: 0 !important;
            height: 100vh !important; overflow: visible !important;
            display: flex !important; flex-direction: column;
            align-items: center; justify-content: center;
            background: white !important; gap: 24px; padding: 40px;
          }
        }
      `}</style>
      <div id='betting-print-sheet'>
        <div className='flex items-center gap-3 mb-2'>
          <img
            src='/logo.png'
            alt='Golf Kraken'
            style={{ height: 48, width: 48, objectFit: "contain" }}
          />
          <div>
            <p
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: "#6b7280",
                margin: 0,
              }}
            >
              Golf Kraken
            </p>
            <p
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: "#102232",
                margin: 0,
              }}
            >
              {pm.eventName}
            </p>
          </div>
        </div>
        <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>
          Scan to place your bets
        </p>
        <QRCodeSVG
          value={`${window.location.origin}/join/${pm.shareCode}`}
          size={220}
          fgColor='#102232'
          bgColor='#ffffff'
        />
        <div
          style={{
            fontFamily: "monospace",
            fontSize: 28,
            fontWeight: 800,
            letterSpacing: "0.3em",
            color: "#102232",
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: "12px 24px",
          }}
        >
          {pm.shareCode}
        </div>
        <p style={{ fontSize: 12, color: "#9ca3af", margin: 0 }}>
          {window.location.origin}/join/{pm.shareCode}
        </p>
        <p style={{ fontSize: 11, color: "#d1d5db", marginTop: 8 }}>
          golfkraken.com
        </p>
      </div>

      {/* ── Share modal ── */}
      {shareModalOpen && pm.shareCode && (
        <div
          className='fixed inset-0 z-40 bg-black/50 flex items-end md:items-center justify-center p-4'
          onClick={() => setShareModalOpen(false)}
        >
          <div
            className='bg-white rounded-3xl w-full max-w-sm p-5 space-y-4'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='flex items-center justify-between'>
              <p className='font-bold text-primary-800 text-base flex items-center gap-2'>
                <Share2 size={16} className='text-secondary-400' />
                Share Betting Link
              </p>
              <button
                onClick={() => setShareModalOpen(false)}
                className='text-gray-400 hover:text-gray-600 p-1'
              >
                <X size={18} />
              </button>
            </div>
            <div className='font-mono text-2xl font-bold tracking-[0.3em] text-primary-800 bg-gray-50 rounded-xl px-4 py-3 border border-gray-200 text-center'>
              {pm.shareCode}
            </div>
            <p className='text-xs text-gray-500 text-center'>
              Anyone with this code or link can submit bets as a guest. Guest
              bets arrive as{" "}
              <span className='text-amber-600 font-semibold'>Pending</span>{" "}
              until you mark them paid.
            </p>
            <button
              type='button'
              onClick={handleCopyShareLink}
              className='flex items-center gap-1.5 w-full justify-center py-2 rounded-xl bg-secondary-400 hover:bg-secondary-500 text-white text-sm font-semibold transition-colors'
            >
              {copiedShareLink ? (
                <>
                  <Check size={14} /> Copied!
                </>
              ) : (
                <>
                  <Copy size={14} /> Copy Link
                </>
              )}
            </button>
            <button
              type='button'
              onClick={handlePrintShare}
              className='flex items-center gap-1.5 w-full justify-center py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold transition-colors'
            >
              <Printer size={14} /> Print / Save as PDF
            </button>
            <div className='flex justify-center'>
              <div className='p-3 bg-white rounded-xl border border-gray-200'>
                <QRCodeSVG
                  value={`${window.location.origin}/join/${pm.shareCode}`}
                  size={160}
                  fgColor='#102232'
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {castViewOpen && (
        <CastViewOverlay pm={pm} onClose={() => setCastViewOpen(false)} />
      )}

      <Header
        title={pm.eventName}
        showBack
        rightAction={
          isOwner ? (
            <div className='flex items-center gap-1'>
              <button
                onClick={() => setCastViewOpen(true)}
                className='p-1.5 rounded-lg hover:bg-white/10 active:bg-white/20'
                aria-label='Display view'
              >
                <Tv size={20} />
              </button>
              {pm.shareCode && (
                <button
                  onClick={() => setShareModalOpen(true)}
                  className='p-1.5 rounded-lg hover:bg-white/10 active:bg-white/20'
                  aria-label='Share betting link'
                >
                  <Share2 size={20} />
                </button>
              )}
            </div>
          ) : undefined
        }
      />

      {/* Status banner with contextual action button */}
      <div
        className='px-4 py-2.5 flex items-center justify-between text-white'
        style={{
          backgroundColor:
            pm.status === "settled"
              ? COLORS.success
              : pm.status === "locked"
                ? COLORS.warning
                : pm.status === "pending"
                  ? COLORS.secondary
                  : COLORS.parimutuel,
        }}
      >
        <span className='text-sm font-semibold'>
          {pm.status === "open"
            ? "🟢 Betting Open"
            : pm.status === "locked"
              ? "🔒 Betting Locked"
              : pm.status === "pending"
                ? "⏳ Betting Not Yet Open"
                : "✅ Settled"}
        </span>
        {isOwner && (
          <div className='flex items-center gap-2'>
            {pm.status === "pending" && (
              <button
                onClick={handleOpen}
                className='flex items-center gap-1.5 text-xs font-semibold bg-white/20 hover:bg-white/30 rounded-lg px-3 py-1.5 transition-colors'
              >
                <Lock size={12} /> Open Betting
              </button>
            )}
            {pm.status === "open" && (
              <>
                <button
                  onClick={handleLock}
                  className='flex items-center gap-1.5 text-xs font-semibold bg-white/20 hover:bg-white/30 rounded-lg px-3 py-1.5 transition-colors'
                >
                  <Lock size={12} /> Lock Betting
                </button>
                <button
                  onClick={handleClose}
                  className='flex items-center gap-1.5 text-xs font-semibold bg-white/10 hover:bg-white/20 rounded-lg px-3 py-1.5 transition-colors'
                >
                  Close Betting
                </button>
              </>
            )}
            {pm.status === "locked" && (
              <>
                <button
                  onClick={handleUnlock}
                  className='flex items-center gap-1.5 text-xs font-semibold bg-white/20 hover:bg-white/30 rounded-lg px-3 py-1.5 transition-colors'
                >
                  <Lock size={12} /> Unlock
                </button>
                {pm.payouts && (
                  <button
                    onClick={handleMarkSettled}
                    className='flex items-center gap-1.5 text-xs font-semibold bg-white/30 hover:bg-white/40 rounded-lg px-3 py-1.5 transition-colors'
                  >
                    <Trophy size={12} /> Mark Settled
                  </button>
                )}
              </>
            )}
            {pm.status === "settled" && (
              <button
                onClick={handleUnsettle}
                className='flex items-center gap-1.5 text-xs font-semibold bg-white/20 hover:bg-white/30 rounded-lg px-3 py-1.5 transition-colors'
              >
                <Lock size={12} /> Unsettle
              </button>
            )}
          </div>
        )}
      </div>

      {/* Pool summary */}
      <div className='grid grid-cols-4 gap-2 px-4 pt-4'>
        {[
          { label: "Win Pool", value: winPool, total: false },
          { label: "Place Pool", value: placePool, total: false },
          { label: "Show Pool", value: showPool, total: false },
          { label: "Total Purse", value: totalPool, total: true },
        ].map(({ label, value, total }) => (
          <div
            key={label}
            className={[
              "rounded-2xl p-3 text-center shadow-sm border",
              total
                ? "bg-green-100 border-green-200"
                : "bg-green-50 border-green-100",
            ].join(" ")}
          >
            <p
              className={[
                "text-xs",
                total ? "text-green-700" : "text-green-600",
              ].join(" ")}
            >
              {label}
            </p>
            <p
              className={[
                "text-base font-bold",
                total ? "text-green-800" : "text-green-700",
              ].join(" ")}
            >
              ${value.toFixed(2)}
            </p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className='flex border-b border-gray-200 bg-white mt-4 sticky top-[60px] md:top-16 z-20'>
        {(["settings", "bets", "results"] as TabId[]).map((t) => (
          <button
            key={t}
            onClick={() => setActiveTab(t)}
            className={[
              "flex-1 py-3 text-xs font-semibold capitalize transition-colors",
              activeTab === t
                ? "text-primary-800 border-b-2 border-primary-800"
                : "text-gray-400 hover:text-gray-600",
            ].join(" ")}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      <div className='px-4 py-4'>
        {/* ── SETTINGS TAB ─────────────────────────────────────────────────── */}
        {activeTab === "settings" && (
          <div className='space-y-3'>
            {/* ─ Bet Limits card ─ */}
            <Card>
              <p className='font-semibold text-primary-800 mb-3'>Bet Limits</p>
              {isOwner && pm.status === "open" ? (
                <form onSubmit={handleUpdateSettings} className='space-y-3'>
                  <div className='grid grid-cols-2 gap-3'>
                    <div>
                      <label className='block text-xs font-semibold text-gray-500 mb-1'>
                        Min Bet ($)
                      </label>
                      <input
                        type='number'
                        min='1'
                        step='1'
                        placeholder='No minimum'
                        value={minBetInput}
                        onChange={(e) =>
                          setMinBetInput(e.target.value.replace(/[^0-9]/g, ""))
                        }
                        className='w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary-400'
                      />
                    </div>
                    <div>
                      <label className='block text-xs font-semibold text-gray-500 mb-1'>
                        Max Bet ($)
                      </label>
                      <input
                        type='number'
                        min='1'
                        step='1'
                        placeholder='No maximum'
                        value={maxBetInput}
                        onChange={(e) =>
                          setMaxBetInput(e.target.value.replace(/[^0-9]/g, ""))
                        }
                        className='w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary-400'
                      />
                    </div>
                  </div>
                  {settingsError && (
                    <p className='text-xs text-red-500'>{settingsError}</p>
                  )}
                  {settingsSaved && (
                    <p className='text-xs text-green-600'>Settings saved.</p>
                  )}
                  <Button
                    type='submit'
                    size='sm'
                    fullWidth
                    loading={settingsLoading}
                  >
                    Save Bet Limits
                  </Button>
                </form>
              ) : (
                <div className='space-y-1'>
                  {pm.minBet != null ? (
                    <p className='text-sm text-gray-600'>
                      Min:{" "}
                      <span className='font-bold text-primary-800'>
                        ${pm.minBet}
                      </span>
                    </p>
                  ) : (
                    <p className='text-sm text-gray-400'>No minimum</p>
                  )}
                  {pm.maxBet != null ? (
                    <p className='text-sm text-gray-600'>
                      Max:{" "}
                      <span className='font-bold text-primary-800'>
                        ${pm.maxBet}
                      </span>
                    </p>
                  ) : (
                    <p className='text-sm text-gray-400'>No maximum</p>
                  )}
                  {isOwner && (
                    <p className='text-xs text-gray-400 pt-1'>
                      Unlock betting to edit limits.
                    </p>
                  )}
                </div>
              )}
            </Card>

            {/* ─ Guest Bet Visibility card ─ */}
            {isOwner && (
              <Card>
                <p className='font-semibold text-primary-800 mb-1'>
                  Guest Bet Visibility
                </p>
                <p className='text-xs text-gray-400 mb-3'>
                  Controls what guests see in their &ldquo;Bets&rdquo; tab. Can
                  be changed at any time.
                </p>
                <label className='flex items-center justify-between cursor-pointer'>
                  <div>
                    <p className='text-sm font-medium text-gray-700'>
                      Show all bets to guests
                    </p>
                    <p className='text-xs text-gray-400 mt-0.5'>
                      {pm.showAllBets
                        ? "Guests can see every bet placed"
                        : "Guests only see bets from their own device"}
                    </p>
                  </div>
                  <button
                    type='button'
                    onClick={handleToggleShowAllBets}
                    className={[
                      "relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none",
                      pm.showAllBets ? "bg-secondary-500" : "bg-gray-200",
                    ].join(" ")}
                    role='switch'
                    aria-checked={pm.showAllBets ?? false}
                  >
                    <span
                      className={[
                        "pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform duration-200",
                        pm.showAllBets ? "translate-x-5" : "translate-x-0",
                      ].join(" ")}
                    />
                  </button>
                </label>
              </Card>
            )}

            {/* ─ Participants card ─ */}
            {isOwner && (pm.status === "open" || pm.status === "pending") && (
              <Card>
                <p className='font-semibold text-primary-800 mb-3 flex items-center gap-2'>
                  <Users size={16} className='text-secondary-400' />
                  Manage Field
                </p>
                <form onSubmit={handleAddParticipant} className='flex gap-2'>
                  <input
                    className='flex-1 border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary-400'
                    placeholder='e.g. 3 - Mystik Dan'
                    value={newParticipantName}
                    onChange={(e) => setNewParticipantName(e.target.value)}
                    required
                  />
                  <Button
                    type='submit'
                    size='sm'
                    loading={addParticipantLoading}
                    leftIcon={<Plus size={14} />}
                  >
                    Add
                  </Button>
                </form>
                {addParticipantError && (
                  <p className='text-xs text-red-500 mt-2'>
                    {addParticipantError}
                  </p>
                )}
              </Card>
            )}

            {sortedParticipants.length === 0 ? (
              <div className='text-center py-8 text-gray-400 text-sm'>
                <p>
                  No participants yet.
                  {isOwner && pm.status === "open" ? " Add some above." : ""}
                </p>
              </div>
            ) : (
              <Card>
                <p className='text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2'>
                  {sortedParticipants.length} participant
                  {sortedParticipants.length !== 1 ? "s" : ""}
                </p>
                <ul className='space-y-1'>
                  {sortedParticipants.map((p) => {
                    const hasBets = pm.bets.some(
                      (b) => b.participantId === p.id,
                    );
                    return (
                      <li
                        key={p.id}
                        className='flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0'
                      >
                        <span
                          className={[
                            "text-sm font-medium flex items-center gap-1.5",
                            p.scratched
                              ? "line-through text-gray-400"
                              : "text-primary-800",
                          ].join(" ")}
                        >
                          {p.name}
                          {p.scratched && (
                            <span
                              className='text-xs font-semibold text-red-500 no-underline'
                              style={{ textDecoration: "none" }}
                            >
                              SCRATCHED
                            </span>
                          )}
                        </span>
                        {isOwner && (
                          <div className='flex items-center gap-1'>
                            {/* Scratch / unscratch — available at any status */}
                            <button
                              onClick={() => handleScratch(p.id, !p.scratched)}
                              title={
                                p.scratched ? "Remove scratch" : "Scratch horse"
                              }
                              className={[
                                "text-xs px-2 py-0.5 rounded-lg font-semibold transition-colors",
                                p.scratched
                                  ? "bg-gray-100 text-gray-500 hover:bg-gray-200"
                                  : "bg-red-50 text-red-400 hover:bg-red-100 hover:text-red-600",
                              ].join(" ")}
                            >
                              {p.scratched ? "Unscratch" : "Scratch"}
                            </button>
                            {/* Remove — only when open and no bets */}
                            {pm.status === "open" && (
                              <button
                                onClick={() => handleRemoveParticipant(p.id)}
                                disabled={hasBets}
                                title={
                                  hasBets
                                    ? "Cannot remove — has bets"
                                    : "Remove"
                                }
                                className={[
                                  "p-1 transition-colors",
                                  hasBets
                                    ? "text-gray-200 cursor-not-allowed"
                                    : "text-red-300 hover:text-red-500",
                                ].join(" ")}
                              >
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </Card>
            )}
          </div>
        )}

        {/* ── BETS TAB ──────────────────────────────────────────────────────── */}
        {activeTab === "bets" && (
          <div className='flex flex-col md:flex-row md:items-start gap-3'>
            {/* ── Left column: bet entry + field coverage ── */}
            <div className='w-full md:w-2/5 shrink-0 space-y-3'>
              {/* Bet entry — owner + open only */}
              {isOwner && pm.status === "pending" && (
                <Card>
                  <div className='flex flex-col items-center py-6 text-center gap-2'>
                    <p className='text-2xl'>⏳</p>
                    <p className='font-semibold text-gray-700'>
                      Betting Not Yet Open
                    </p>
                    <p className='text-xs text-gray-400'>
                      Click <strong>Open Betting</strong> in the banner above to
                      start accepting bets.
                    </p>
                  </div>
                </Card>
              )}
              {/* Bet entry — owner + open only */}
              {isOwner && pm.status === "open" && (
                <Card>
                  {!activeBettor ? (
                    <div>
                      <p className='font-semibold text-primary-800 mb-2 flex items-center gap-2'>
                        <UserCheck size={16} className='text-secondary-400' />
                        Who is placing bets?
                      </p>
                      <div className='relative' ref={nameRef}>
                        <input
                          className='w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary-400'
                          placeholder='Type or select a name…'
                          value={nameInput}
                          onChange={(e) => {
                            setNameInput(e.target.value);
                            setNameDropdownOpen(true);
                          }}
                          onFocus={() => setNameDropdownOpen(true)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && nameInput.trim()) {
                              e.preventDefault();
                              selectBettor(nameInput.trim());
                            }
                          }}
                        />
                        {nameDropdownOpen && (
                          <div className='absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto'>
                            {nameInput.trim() &&
                              !existingBettorNames.some(
                                (n) =>
                                  n.toLowerCase() ===
                                  nameInput.trim().toLowerCase(),
                              ) && (
                                <button
                                  className='w-full text-left px-3 py-2 text-sm text-secondary-600 hover:bg-secondary-50 font-medium border-b border-gray-100'
                                  onMouseDown={() =>
                                    selectBettor(nameInput.trim())
                                  }
                                >
                                  + Add "{nameInput.trim()}"
                                </button>
                              )}
                            {filteredBettorNames.length > 0
                              ? filteredBettorNames.map((n) => (
                                  <button
                                    key={n}
                                    className='w-full text-left px-3 py-2 text-sm hover:bg-gray-50'
                                    onMouseDown={() => selectBettor(n)}
                                  >
                                    {n}
                                  </button>
                                ))
                              : !nameInput.trim() && (
                                  <p className='px-3 py-2 text-xs text-gray-400'>
                                    No previous bettors — type a name above
                                  </p>
                                )}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className='flex items-center justify-between mb-3 bg-secondary-50 rounded-xl px-3 py-2'>
                        <div className='flex items-center gap-2'>
                          <UserCheck size={15} className='text-secondary-500' />
                          <span className='text-sm font-semibold text-secondary-700'>
                            {activeBettor}
                          </span>
                        </div>
                        <button
                          onClick={clearBettor}
                          className='text-gray-400 hover:text-gray-600 p-1'
                          title='Change bettor'
                        >
                          <X size={14} />
                        </button>
                      </div>

                      {sortedParticipants.length === 0 ? (
                        <p className='text-xs text-gray-400 text-center py-4'>
                          No participants in the field yet. Add some on the
                          Settings tab first.
                        </p>
                      ) : (
                        <form onSubmit={handleAddBet} className='space-y-3'>
                          <div>
                            <label className='block text-xs font-semibold text-gray-500 mb-1'>
                              Participant
                            </label>
                            <select
                              className='w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary-400 bg-white'
                              value={selectedParticipantId}
                              onChange={(e) =>
                                setSelectedParticipantId(e.target.value)
                              }
                              required
                            >
                              <option value=''>Select a participant…</option>
                              {sortedParticipants.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className='grid grid-cols-3 gap-2'>
                            <Input
                              label='Win $'
                              type='number'
                              min='0'
                              step='1'
                              value={winAmt}
                              onChange={(e) =>
                                setWinAmt(e.target.value.replace(/[^0-9]/g, ""))
                              }
                              placeholder='0'
                            />
                            <Input
                              label='Place $'
                              type='number'
                              min='0'
                              step='1'
                              value={placeAmt}
                              onChange={(e) =>
                                setPlaceAmt(
                                  e.target.value.replace(/[^0-9]/g, ""),
                                )
                              }
                              placeholder='0'
                            />
                            <Input
                              label='Show $'
                              type='number'
                              min='0'
                              step='1'
                              value={showAmt}
                              onChange={(e) =>
                                setShowAmt(
                                  e.target.value.replace(/[^0-9]/g, ""),
                                )
                              }
                              placeholder='0'
                            />
                          </div>

                          {betError && (
                            <p className='text-xs text-red-500'>{betError}</p>
                          )}

                          {/* Paid toggle */}
                          <button
                            type='button'
                            onClick={() => setBetPaid((v) => !v)}
                            className={`w-full flex items-center justify-between px-3 py-2 rounded-xl border text-sm font-medium transition-colors ${
                              betPaid
                                ? "bg-green-50 border-green-300 text-green-700"
                                : "bg-amber-50 border-amber-300 text-amber-700"
                            }`}
                          >
                            <span>
                              {betPaid ? "✓ Paid" : "⏳ Pending payment"}
                            </span>
                            <span className='text-xs opacity-60'>
                              tap to toggle
                            </span>
                          </button>

                          <Button
                            type='submit'
                            fullWidth
                            loading={betLoading}
                            size='sm'
                            leftIcon={<Plus size={14} />}
                          >
                            Add Bet for {activeBettor}
                          </Button>
                          <button
                            type='button'
                            onClick={clearBettor}
                            className='w-full text-xs text-gray-400 hover:text-gray-600 py-1'
                          >
                            Done with {activeBettor} — switch bettor
                          </button>
                        </form>
                      )}
                    </div>
                  )}
                </Card>
              )}

              {/* ── Participant bet coverage summary — always visible ── */}
              {sortedParticipants.length > 0 && (
                <div className='rounded-2xl border border-slate-200 bg-slate-50 shadow-sm p-4'>
                  <p className='text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2'>
                    Field Coverage
                  </p>
                  <table className='w-full text-xs'>
                    <thead>
                      <tr className='border-b border-gray-100'>
                        <th className='text-left py-1 font-semibold text-gray-400 pr-3'>
                          Horse
                        </th>
                        <th className='text-right py-1 font-semibold text-gray-400 w-14 px-2'>
                          Win
                        </th>
                        <th className='text-right py-1 font-semibold text-gray-400 w-14 px-2'>
                          Pl
                        </th>
                        <th className='text-right py-1 font-semibold text-gray-400 w-14 pl-2'>
                          Sh
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedParticipants.map((p) => {
                        const pBets = pm.bets.filter(
                          (b) => b.participantId === p.id,
                        );
                        const winTotal = pBets.reduce(
                          (s, b) => s + b.winAmount,
                          0,
                        );
                        const placeTotal = pBets.reduce(
                          (s, b) => s + b.placeAmount,
                          0,
                        );
                        const showTotal = pBets.reduce(
                          (s, b) => s + b.showAmount,
                          0,
                        );
                        const hasBets = pBets.length > 0;
                        return (
                          <tr
                            key={p.id}
                            className={[
                              "border-b border-gray-50 last:border-0",
                              p.scratched ? "opacity-40" : "",
                            ].join(" ")}
                          >
                            <td
                              className={[
                                "py-1.5 pr-2 font-medium truncate max-w-0",
                                hasBets ? "text-primary-800" : "text-gray-400",
                                p.scratched ? "line-through" : "",
                              ].join(" ")}
                              style={{ maxWidth: "1px", width: "100%" }}
                            >
                              <span className='block truncate'>{p.name}</span>
                            </td>
                            <td
                              className={[
                                "py-1.5 text-right tabular-nums w-14 px-2",
                                winTotal > 0
                                  ? "text-primary-700 font-semibold"
                                  : "text-gray-300",
                              ].join(" ")}
                            >
                              {winTotal > 0 ? `$${winTotal}` : "—"}
                            </td>
                            <td
                              className={[
                                "py-1.5 text-right tabular-nums w-14 px-2",
                                placeTotal > 0
                                  ? "text-primary-700 font-semibold"
                                  : "text-gray-300",
                              ].join(" ")}
                            >
                              {placeTotal > 0 ? `$${placeTotal}` : "—"}
                            </td>
                            <td
                              className={[
                                "py-1.5 text-right tabular-nums w-14 pl-2",
                                showTotal > 0
                                  ? "text-primary-700 font-semibold"
                                  : "text-gray-300",
                              ].join(" ")}
                            >
                              {showTotal > 0 ? `$${showTotal}` : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* ── Right column: filters + bets list ── */}
            <div className='flex-1 min-w-0 space-y-3'>
              {/* ── New bet flash notification ── */}
              {newBetFlash > 0 && (
                <div className='flex items-center justify-between bg-green-50 border border-green-300 rounded-xl px-3 py-2.5 animate-pulse'>
                  <span className='text-xs font-semibold text-green-700'>
                    🔔 {newBetFlash} new bet{newBetFlash !== 1 ? "s" : ""}{" "}
                    received!
                  </span>
                  <button
                    onClick={() => setNewBetFlash(0)}
                    className='text-xs text-green-600 hover:text-green-800 font-semibold'
                  >
                    Dismiss
                  </button>
                </div>
              )}

              {/* ── Confirmed bets summary ── */}
              {isOwner &&
                (() => {
                  const confirmedCount = pm.bets.filter(
                    (b) => b.paid !== false && !b.voided,
                  ).length;
                  if (confirmedCount === 0) return null;
                  return (
                    <div className='flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-3 py-2.5'>
                      <span className='text-xs font-semibold text-green-700'>
                        ✅ {confirmedCount} confirmed bet
                        {confirmedCount !== 1 ? "s" : ""}
                      </span>
                    </div>
                  );
                })()}

              {/* ── Pending payments banner ── */}
              {isOwner &&
                (() => {
                  const pendingCount = pm.bets.filter(
                    (b) => b.paid === false && !b.voided,
                  ).length;
                  if (pendingCount === 0) return null;
                  return (
                    <div className='flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5'>
                      <span className='text-xs font-semibold text-amber-700'>
                        ⏳ {pendingCount} pending payment
                        {pendingCount !== 1 ? "s" : ""}
                      </span>
                      <button
                        onClick={() =>
                          pendingPaymentsRef.current?.scrollIntoView({
                            behavior: "smooth",
                          })
                        }
                        className='text-xs text-amber-600 hover:text-amber-800 font-semibold underline'
                      >
                        View ↓
                      </button>
                    </div>
                  );
                })()}

              {pm.bets.length > 0 && (
                <div className='bg-white rounded-2xl border border-gray-100 shadow-sm px-3 py-3 space-y-2'>
                  <p className='text-xs font-semibold text-gray-400 uppercase tracking-wide'>
                    Filters
                  </p>
                  <input
                    className='w-full border border-gray-200 rounded-xl px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-secondary-400'
                    placeholder='Filter by bettor name…'
                    value={filterName}
                    onChange={(e) => setFilterName(e.target.value)}
                  />
                  {sortedParticipants.length > 0 && (
                    <select
                      className='w-full border border-gray-200 rounded-xl px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-secondary-400 bg-white'
                      value={filterParticipantId}
                      onChange={(e) => setFilterParticipantId(e.target.value)}
                    >
                      <option value=''>All horses / participants</option>
                      {sortedParticipants.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.scratched ? `❌ ${p.name}` : p.name}
                        </option>
                      ))}
                    </select>
                  )}
                  <div className='flex gap-1.5'>
                    {(["all", "win", "place", "show"] as const).map((t) => (
                      <button
                        key={t}
                        onClick={() => setFilterBetType(t)}
                        className={[
                          "flex-1 py-1 rounded-xl text-xs font-semibold capitalize transition-colors",
                          filterBetType === t
                            ? "bg-primary-800 text-white"
                            : "bg-gray-100 text-gray-500 hover:bg-gray-200",
                        ].join(" ")}
                      >
                        {t === "all" ? "All" : t}
                      </button>
                    ))}
                  </div>
                  {filtersActive && (
                    <button
                      className='text-xs text-secondary-500 hover:text-secondary-700'
                      onClick={() => {
                        setFilterName("");
                        setFilterBetType("all");
                        setFilterParticipantId("");
                      }}
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              )}

              {pm.bets.length === 0 ? (
                <div className='text-center py-8 text-gray-400'>
                  <p>No bets yet.</p>
                </div>
              ) : filteredBetsByBettor.size === 0 ? (
                <div className='text-center py-8 text-gray-400 text-sm'>
                  <p>No bets match the current filters.</p>
                </div>
              ) : (
                <div className='space-y-3'>
                  {Array.from(filteredBetsByBettor.entries()).map(
                    ([name, bets]) => {
                      const total = bets
                        .filter((b) => !b.voided)
                        .reduce(
                          (s, b) =>
                            s + b.winAmount + b.placeAmount + b.showAmount,
                          0,
                        );
                      const pendingCount = bets.filter(
                        (b) => !b.voided && b.paid === false,
                      ).length;
                      const nonVoidedBets = bets.filter((b) => !b.voided);
                      const unpaidBetCount = nonVoidedBets.filter(
                        (b) => b.paid === false,
                      ).length;
                      const paidBetCount =
                        nonVoidedBets.length - unpaidBetCount;
                      const cardAccent =
                        nonVoidedBets.length === 0
                          ? COLORS.parimutuel
                          : unpaidBetCount === 0
                            ? COLORS.success
                            : paidBetCount === 0
                              ? COLORS.error
                              : "#0ea5e9"; // sky-500 — clearly distinct from red/green
                      return (
                        <Card key={name} accent={cardAccent}>
                          <div className='flex items-center justify-between mb-2'>
                            <span className='font-semibold text-primary-800'>
                              {name}
                            </span>
                            <div className='flex items-center gap-2'>
                              <span className='text-xs text-gray-400'>
                                {bets.length} bet{bets.length !== 1 ? "s" : ""}{" "}
                                · ${total.toFixed(2)}
                                {pendingCount > 0 && (
                                  <span className='ml-1 text-amber-500'>
                                    ({pendingCount} pending · $
                                    {bets
                                      .filter(
                                        (b) => !b.voided && b.paid === false,
                                      )
                                      .reduce(
                                        (s, b) =>
                                          s +
                                          b.winAmount +
                                          b.placeAmount +
                                          b.showAmount,
                                        0,
                                      )}
                                    )
                                  </span>
                                )}
                              </span>
                              {isOwner && pm.status === "open" && (
                                <button
                                  className='text-xs text-secondary-500 hover:text-secondary-700 font-medium'
                                  onClick={() => selectBettor(name)}
                                >
                                  + Add more
                                </button>
                              )}
                            </div>
                          </div>
                          <table className='w-full text-sm'>
                            <thead>
                              <tr className='text-xs text-gray-400 border-b'>
                                <th className='text-left py-1 font-medium'>
                                  Participant
                                </th>
                                <th className='text-right py-1 font-medium'>
                                  Win
                                </th>
                                <th className='text-right py-1 font-medium'>
                                  Place
                                </th>
                                <th className='text-right py-1 font-medium'>
                                  Show
                                </th>
                                {isOwner && pm.status === "open" && <th />}
                                {isOwner && <th className='w-6' />}
                              </tr>
                            </thead>
                            <tbody>
                              {bets.map((bet) => (
                                <tr
                                  key={bet.id}
                                  className={`border-b border-gray-50 last:border-0 ${bet.voided || scratchedParticipantIds.has(bet.participantId) ? "opacity-40" : ""}`}
                                >
                                  <td className='py-1.5 text-xs text-primary-800 font-medium'>
                                    <span
                                      className={
                                        bet.voided ||
                                        scratchedParticipantIds.has(
                                          bet.participantId,
                                        )
                                          ? "line-through"
                                          : ""
                                      }
                                    >
                                      {bet.participantName ??
                                        `#${bet.horseNumber}${bet.horseName ? ` ${bet.horseName}` : ""}`}
                                    </span>
                                    {bet.source === "guest" && (
                                      <span className='ml-1 text-[10px] bg-sky-100 text-sky-600 rounded px-1 font-semibold'>
                                        🔗 guest
                                      </span>
                                    )}
                                    {bet.source === "admin" && (
                                      <span className='ml-1 text-[10px] bg-gray-100 text-gray-500 rounded px-1'>
                                        ⚙️ admin
                                      </span>
                                    )}
                                    {bet.voided && (
                                      <span className='ml-1 text-[10px] bg-red-100 text-red-500 rounded px-1'>
                                        voided
                                      </span>
                                    )}
                                    {!bet.voided &&
                                      scratchedParticipantIds.has(
                                        bet.participantId,
                                      ) && (
                                        <span className='ml-1 text-[10px] bg-orange-100 text-orange-600 rounded px-1'>
                                          scratched
                                        </span>
                                      )}
                                    {!bet.voided &&
                                      !scratchedParticipantIds.has(
                                        bet.participantId,
                                      ) &&
                                      bet.paid === false && (
                                        <span className='ml-1 text-[10px] bg-amber-100 text-amber-600 rounded px-1'>
                                          pending
                                        </span>
                                      )}
                                  </td>
                                  <td className='py-1.5 text-right text-xs'>
                                    {bet.winAmount > 0
                                      ? `$${bet.winAmount}`
                                      : "—"}
                                  </td>
                                  <td className='py-1.5 text-right text-xs'>
                                    {bet.placeAmount > 0
                                      ? `$${bet.placeAmount}`
                                      : "—"}
                                  </td>
                                  <td className='py-1.5 text-right text-xs'>
                                    {bet.showAmount > 0
                                      ? `$${bet.showAmount}`
                                      : "—"}
                                  </td>
                                  {isOwner && pm.status === "open" && (
                                    <td className='py-1.5 text-right'>
                                      <button
                                        onClick={() => handleRemoveBet(bet.id)}
                                        className='text-red-300 hover:text-red-500 p-1'
                                      >
                                        <Trash2 size={13} />
                                      </button>
                                    </td>
                                  )}
                                  {/* Paid/unpaid toggle — always visible to owner */}
                                  {isOwner && !bet.voided && (
                                    <td className='py-1.5 text-right pl-0.5'>
                                      {bet.paid !== false ? (
                                        <button
                                          title='Mark as unpaid'
                                          disabled={markPaidLoading === bet.id}
                                          onClick={() =>
                                            handleMarkPaid(bet.id, false)
                                          }
                                          className='group p-1 text-green-400 hover:text-red-400 transition-colors disabled:opacity-40'
                                        >
                                          <CheckCircle2
                                            size={13}
                                            className='group-hover:hidden'
                                          />
                                          <RotateCcw
                                            size={13}
                                            className='hidden group-hover:block'
                                          />
                                        </button>
                                      ) : (
                                        <button
                                          title='Mark as paid'
                                          disabled={markPaidLoading === bet.id}
                                          onClick={() =>
                                            handleMarkPaid(bet.id, true)
                                          }
                                          className='p-1 text-amber-300 hover:text-green-500 transition-colors disabled:opacity-40'
                                        >
                                          <CheckCircle2 size={13} />
                                        </button>
                                      )}
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Card>
                      );
                    },
                  )}
                </div>
              )}

              {isOwner && pm.status === "open" && pm.bets.length > 0 && (
                <p className='text-xs text-gray-400 text-center pt-1'>
                  Lock betting from the status banner above when ready.
                </p>
              )}

              {/* ── Pending Payments table (bottom) ── */}
              {isOwner &&
                (() => {
                  const pendingBets = pm.bets.filter(
                    (b) =>
                      b.paid === false &&
                      !b.voided &&
                      !scratchedParticipantIds.has(b.participantId),
                  );
                  if (pendingBets.length === 0) return null;

                  // Group by bettor while preserving insertion order
                  const byBettor = new Map<string, PariMutuelBet[]>();
                  for (const bet of pendingBets) {
                    const arr = byBettor.get(bet.bettorName) ?? [];
                    arr.push(bet);
                    byBettor.set(bet.bettorName, arr);
                  }

                  return (
                    <div ref={pendingPaymentsRef}>
                      {/* Section header band */}
                      <div className='flex items-center gap-2 -mx-0 bg-amber-500 rounded-t-2xl px-4 py-2.5 mt-4'>
                        <span className='text-sm font-bold text-white tracking-wide'>
                          ⏳ PENDING PAYMENTS
                        </span>
                        <span className='ml-auto text-xs font-semibold bg-white/25 text-white rounded-full px-2 py-0.5'>
                          {pendingBets.length} bet
                          {pendingBets.length !== 1 ? "s" : ""}
                        </span>
                      </div>

                      {/* Table */}
                      <div className='bg-amber-50 border border-amber-200 border-t-0 rounded-b-2xl overflow-hidden'>
                        <table className='w-full text-sm'>
                          <thead>
                            <tr className='border-b border-amber-200 bg-amber-100/60'>
                              <th className='text-left px-4 py-2 text-xs font-semibold text-amber-800'>
                                Bettor
                              </th>
                              <th className='text-left px-2 py-2 text-xs font-semibold text-amber-800'>
                                Horse
                              </th>
                              <th className='text-right px-2 py-2 text-xs font-semibold text-amber-800'>
                                Total
                              </th>
                              <th className='px-3 py-2' />
                            </tr>
                          </thead>
                          <tbody>
                            {Array.from(byBettor.entries()).map(
                              ([bettorName, bettorBets], groupIdx) => {
                                const isLastGroup =
                                  groupIdx === byBettor.size - 1;
                                const bettorTotal = bettorBets.reduce(
                                  (s, b) =>
                                    s +
                                    b.winAmount +
                                    b.placeAmount +
                                    b.showAmount,
                                  0,
                                );
                                return [
                                  ...bettorBets.map((bet, betIdx) => {
                                    const total =
                                      bet.winAmount +
                                      bet.placeAmount +
                                      bet.showAmount;
                                    const isFirstInGroup = betIdx === 0;
                                    return (
                                      <tr
                                        key={bet.id}
                                        className='border-b border-amber-100 transition-colors hover:bg-amber-100/50'
                                      >
                                        <td className='px-4 py-2 font-semibold text-primary-800 text-xs align-middle'>
                                          {isFirstInGroup ? bettorName : ""}
                                        </td>
                                        <td className='px-2 py-2 text-xs align-middle truncate max-w-[120px]'>
                                          <span className='text-gray-700'>
                                            {bet.participantName}
                                          </span>
                                        </td>
                                        <td className='px-2 py-2 text-xs font-semibold text-right tabular-nums align-middle'>
                                          <span className='text-primary-800'>
                                            ${total}
                                          </span>
                                        </td>
                                        <td className='px-3 py-1.5 text-right align-middle'>
                                          <button
                                            disabled={
                                              markPaidLoading === bet.id
                                            }
                                            onClick={() =>
                                              handleMarkPaid(bet.id, true)
                                            }
                                            className='text-[11px] bg-green-500 hover:bg-green-600 text-white px-2.5 py-1 rounded-lg font-semibold disabled:opacity-50 transition-colors whitespace-nowrap'
                                          >
                                            {markPaidLoading === bet.id
                                              ? "…"
                                              : "Mark Paid"}
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  }),
                                  // Subtotal row per bettor
                                  <tr
                                    key={`${bettorName}-subtotal`}
                                    className={[
                                      "border-b border-amber-100",
                                      isLastGroup
                                        ? ""
                                        : "border-b-[3px] border-b-amber-400",
                                    ].join(" ")}
                                  >
                                    <td className='px-4 py-2' />
                                    <td className='px-2 py-2 text-xs font-semibold text-gray-500 italic'>
                                      Total Owed
                                    </td>
                                    <td className='px-2 py-2 text-xs font-bold text-amber-600 text-right tabular-nums'>
                                      ${bettorTotal}
                                    </td>
                                    <td />
                                  </tr>,
                                ];
                              },
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}

              {/* ── Scratched Bets / Refunds section ── */}
              {isOwner &&
                (() => {
                  const scratchedPaidBets = pm.bets.filter(
                    (b) =>
                      !b.voided &&
                      b.paid === true &&
                      scratchedParticipantIds.has(b.participantId),
                  );
                  if (scratchedPaidBets.length === 0) return null;

                  const byBettor = new Map<string, PariMutuelBet[]>();
                  for (const bet of scratchedPaidBets) {
                    const arr = byBettor.get(bet.bettorName) ?? [];
                    arr.push(bet);
                    byBettor.set(bet.bettorName, arr);
                  }

                  return (
                    <div className='mt-4'>
                      <div className='flex items-center gap-2 bg-red-500 rounded-t-2xl px-4 py-2.5'>
                        <span className='text-sm font-bold text-white tracking-wide'>
                          💸 SCRATCHED BET REFUNDS
                        </span>
                        <span className='ml-auto text-xs font-semibold bg-white/25 text-white rounded-full px-2 py-0.5'>
                          {scratchedPaidBets.length} bet
                          {scratchedPaidBets.length !== 1 ? "s" : ""}
                        </span>
                      </div>
                      <div className='bg-red-50 border border-red-200 border-t-0 rounded-b-2xl overflow-hidden'>
                        <p className='px-4 py-2 text-[11px] text-red-700 font-medium border-b border-red-100'>
                          These bets were paid and placed on scratched
                          participants — refunds are owed to the bettors below.
                        </p>
                        <table className='w-full text-sm'>
                          <thead>
                            <tr className='border-b border-red-200 bg-red-100/60'>
                              <th className='text-left px-4 py-2 text-xs font-semibold text-red-800'>
                                Bettor
                              </th>
                              <th className='text-left px-2 py-2 text-xs font-semibold text-red-800'>
                                Horse
                              </th>
                              <th className='text-right px-2 py-2 text-xs font-semibold text-red-800'>
                                Win
                              </th>
                              <th className='text-right px-2 py-2 text-xs font-semibold text-red-800'>
                                Pl
                              </th>
                              <th className='text-right px-3 py-2 text-xs font-semibold text-red-800'>
                                Sh
                              </th>
                              <th className='text-right px-3 py-2 text-xs font-semibold text-red-800'>
                                Total
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {Array.from(byBettor.entries()).map(
                              ([bettorName, bets], groupIdx) => {
                                const isLastGroup =
                                  groupIdx === byBettor.size - 1;
                                const bettorTotal = bets.reduce(
                                  (s, b) =>
                                    s +
                                    b.winAmount +
                                    b.placeAmount +
                                    b.showAmount,
                                  0,
                                );
                                return [
                                  ...bets.map((bet, i) => (
                                    <tr
                                      key={bet.id}
                                      className='border-b border-red-100 last:border-0 hover:bg-red-100/40 transition-colors'
                                    >
                                      <td className='px-4 py-2 text-xs font-semibold text-primary-800'>
                                        {i === 0 ? bettorName : ""}
                                      </td>
                                      <td className='px-2 py-2 text-xs text-gray-500 line-through'>
                                        {bet.participantName}
                                      </td>
                                      <td className='px-2 py-2 text-xs text-right tabular-nums text-gray-600'>
                                        {bet.winAmount > 0
                                          ? `$${bet.winAmount}`
                                          : "—"}
                                      </td>
                                      <td className='px-2 py-2 text-xs text-right tabular-nums text-gray-600'>
                                        {bet.placeAmount > 0
                                          ? `$${bet.placeAmount}`
                                          : "—"}
                                      </td>
                                      <td className='px-2 py-2 text-xs text-right tabular-nums text-gray-600'>
                                        {bet.showAmount > 0
                                          ? `$${bet.showAmount}`
                                          : "—"}
                                      </td>
                                      <td className='px-3 py-2 text-xs text-right tabular-nums font-semibold text-red-700'>
                                        $
                                        {bet.winAmount +
                                          bet.placeAmount +
                                          bet.showAmount}
                                      </td>
                                    </tr>
                                  )),
                                  <tr
                                    key={`${bettorName}-refund-total`}
                                    className={[
                                      "border-b border-red-100",
                                      isLastGroup
                                        ? ""
                                        : "border-b-[3px] border-b-red-300",
                                    ].join(" ")}
                                  >
                                    <td className='px-4 py-2' />
                                    <td
                                      colSpan={4}
                                      className='px-2 py-2 text-xs font-semibold text-gray-500 italic'
                                    >
                                      Refund Total
                                    </td>
                                    <td className='px-3 py-2 text-xs font-bold text-red-600 text-right tabular-nums'>
                                      ${bettorTotal}
                                    </td>
                                  </tr>,
                                ];
                              },
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}
            </div>
          </div>
        )}

        {/* ── RESULTS TAB ───────────────────────────────────────────────────── */}
        {activeTab === "results" && (
          <div className='space-y-4'>
            {pm.status === "open" ? (
              <div className='text-center py-8 text-gray-400 text-sm'>
                Lock betting first, then enter finishing positions.
              </div>
            ) : !isOwner && !pm.parameters ? (
              <div className='text-center py-8 text-gray-400 text-sm'>
                Results not yet entered.
              </div>
            ) : pm.parameters && !editingParams ? (
              <Card>
                <div className='flex items-center justify-between mb-3'>
                  <h3 className='font-bold text-primary-800'>
                    Final Finishing Order
                  </h3>
                  {isOwner && (
                    <button
                      className='text-xs text-secondary-500 hover:text-secondary-700 font-semibold'
                      onClick={() => setEditingParams(true)}
                    >
                      Edit
                    </button>
                  )}
                </div>
                <div className='space-y-2'>
                  {[
                    {
                      label: "🥇 Win (1st)",
                      id: pm.parameters.winParticipantId,
                    },
                    {
                      label: "🥈 Place (2nd)",
                      id: pm.parameters.placeParticipantId,
                    },
                    {
                      label: "🥉 Show (3rd)",
                      id: pm.parameters.showParticipantId,
                    },
                  ].map(({ label, id }) => (
                    <div
                      key={label}
                      className='flex items-center justify-between py-2 border-b border-gray-100'
                    >
                      <span className='text-sm text-gray-600'>{label}</span>
                      <span className='font-bold text-primary-800'>
                        {participantById.get(id)?.name ?? id}
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
            ) : isOwner ? (
              <Card>
                <div className='flex items-center justify-between mb-1'>
                  <h3 className='font-bold text-primary-800'>
                    {pm.parameters ? "Edit Results" : "Enter Results"}
                  </h3>
                  {pm.parameters && (
                    <button
                      className='text-xs text-gray-400 hover:text-gray-600'
                      onClick={() => setEditingParams(false)}
                    >
                      Cancel
                    </button>
                  )}
                </div>
                <p className='text-xs text-gray-500 mb-4'>
                  Select the finishing positions and save.
                </p>
                <form onSubmit={handleSaveResults} className='space-y-3'>
                  {[
                    {
                      label: "🥇 Win – 1st Place",
                      value: winParticipantId,
                      onChange: setWinParticipantId,
                    },
                    {
                      label: "🥈 Place – 2nd Place",
                      value: placeParticipantId,
                      onChange: setPlaceParticipantId,
                    },
                    {
                      label: "🥉 Show – 3rd Place",
                      value: showParticipantId,
                      onChange: setShowParticipantId,
                    },
                  ].map(({ label, value, onChange }) => (
                    <div key={label}>
                      <label className='block text-xs font-semibold text-gray-500 mb-1'>
                        {label}
                      </label>
                      <select
                        className='w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary-400 bg-white'
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        required
                      >
                        <option value=''>Select participant…</option>
                        {sortedParticipants.map((p) => (
                          <option
                            key={p.id}
                            value={p.id}
                            disabled={p.scratched}
                          >
                            {p.scratched ? `❌ ${p.name} (SCR)` : p.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                  {paramError && (
                    <p className='text-xs text-red-500'>{paramError}</p>
                  )}
                  <Button
                    type='submit'
                    fullWidth
                    loading={paramLoading}
                    leftIcon={<Trophy size={16} />}
                  >
                    Save Results
                  </Button>
                </form>
              </Card>
            ) : null}

            {/* ── Calculate Payouts (results saved, no payouts yet) ── */}
            {isOwner && pm.parameters && !editingParams && !pm.payouts && (
              <Card>
                <h3 className='font-bold text-primary-800 mb-1'>
                  Calculate Payouts
                </h3>
                <p className='text-xs text-gray-500 mb-4'>
                  Results are saved. Click below to calculate and distribute the
                  pool.
                </p>
                {paramError && (
                  <p className='text-xs text-red-500 mb-2'>{paramError}</p>
                )}
                <Button
                  fullWidth
                  loading={paramLoading}
                  leftIcon={<Trophy size={16} />}
                  onClick={handleCalculatePayouts}
                >
                  Calculate Payouts
                </Button>
              </Card>
            )}

            {/* ── Payouts output (shown once calculated) ── */}
            {pm.payouts && (
              <div className='space-y-4'>
                {isOwner && pm.status !== "settled" && (
                  <div className='flex justify-end'>
                    <button
                      disabled={paramLoading}
                      className='text-xs text-secondary-500 hover:text-secondary-700 font-semibold disabled:opacity-50'
                      onClick={handleCalculatePayouts}
                    >
                      {paramLoading ? "Recalculating…" : "Recalculate"}
                    </button>
                  </div>
                )}
                <div className='grid grid-cols-4 gap-2 text-sm'>
                  {[
                    { label: "Win Pool", v: pm.payouts.winPool, total: false },
                    {
                      label: "Place Pool",
                      v: pm.payouts.placePool,
                      total: false,
                    },
                    {
                      label: "Show Pool",
                      v: pm.payouts.showPool,
                      total: false,
                    },
                    { label: "Total Purse", v: totalPool, total: true },
                  ].map(({ label, v, total }) => (
                    <div
                      key={label}
                      className={[
                        "rounded-2xl p-3 shadow-sm border",
                        total
                          ? "bg-green-100 border-green-200"
                          : "bg-green-50 border-green-100",
                      ].join(" ")}
                    >
                      <p
                        className={[
                          "text-xs",
                          total ? "text-green-700" : "text-green-600",
                        ].join(" ")}
                      >
                        {label}
                      </p>
                      <p
                        className={[
                          "font-bold",
                          total ? "text-green-800" : "text-green-700",
                        ].join(" ")}
                      >
                        ${v.toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>

                {(() => {
                  const totalPayout = pm.payouts.payoutRows
                    .filter((r: PariMutuelPayoutRow) => !r.scratched)
                    .reduce(
                      (s: number, r: PariMutuelPayoutRow) => s + r.totalPayout,
                      0,
                    );
                  const totalRefunds = pm.payouts.payoutRows
                    .filter((r: PariMutuelPayoutRow) => r.scratched)
                    .reduce(
                      (s: number, r: PariMutuelPayoutRow) => s + r.totalPayout,
                      0,
                    );
                  const {
                    unawardedWin = 0,
                    unawardedPlace = 0,
                    unawardedShow = 0,
                  } = pm.payouts;
                  const totalUnawarded =
                    unawardedWin + unawardedPlace + unawardedShow;

                  const cols = [
                    <div
                      key='payout'
                      className='bg-green-50 rounded-2xl p-3 shadow-sm border border-green-100'
                    >
                      <p className='text-xs text-green-600'>Total Bet Payout</p>
                      <p className='font-bold text-green-700'>
                        ${totalPayout.toFixed(2)}
                      </p>
                    </div>,
                    totalUnawarded > 0 && (
                      <div
                        key='unawarded'
                        className='bg-amber-50 rounded-2xl p-3 shadow-sm border border-amber-100'
                      >
                        <p className='text-xs text-amber-600'>Unawarded Pool</p>
                        <p className='font-bold text-amber-700'>
                          ${totalUnawarded.toFixed(2)}
                        </p>
                      </div>
                    ),
                    totalRefunds > 0 && (
                      <div
                        key='refunds'
                        className='bg-red-50 rounded-2xl p-3 shadow-sm border border-red-100'
                      >
                        <p className='text-xs text-red-400'>Total Refunds</p>
                        <p className='font-bold text-red-600'>
                          ${totalRefunds.toFixed(2)}
                        </p>
                      </div>
                    ),
                  ].filter(Boolean);

                  return (
                    <div
                      className={`grid grid-cols-${cols.length} gap-2 text-sm`}
                    >
                      {cols}
                    </div>
                  );
                })()}

                {(() => {
                  const {
                    unawardedWin = 0,
                    unawardedPlace = 0,
                    unawardedShow = 0,
                  } = pm.payouts;
                  const totalUnawarded =
                    unawardedWin + unawardedPlace + unawardedShow;
                  if (totalUnawarded === 0 && !pm.reconciliation) return null;

                  if (pm.reconciliation) {
                    return (
                      <div className='rounded-2xl border border-green-200 bg-green-50 px-4 py-3 space-y-2'>
                        <p className='text-xs font-bold text-green-800 uppercase tracking-wide'>
                          🎲 Blind Draw Complete — $
                          {pm.reconciliation.totalUnawarded.toFixed(2)}{" "}
                          Reconciled
                        </p>
                        <p className='text-xs text-green-700'>
                          {pm.reconciliation.winners.length} winner
                          {pm.reconciliation.winners.length !== 1
                            ? "s were"
                            : " was"}{" "}
                          randomly selected to split the unawarded funds:
                        </p>
                        <ul className='space-y-1.5'>
                          {pm.reconciliation.winners.map((w, i) => (
                            <li
                              key={w.bettorName}
                              className='flex items-center justify-between bg-white rounded-xl px-3 py-2 border border-green-100'
                            >
                              <span className='text-sm font-semibold text-green-900'>
                                🏆 #{i + 1} — {w.bettorName}
                              </span>
                              <span className='text-sm font-bold text-green-700'>
                                +${w.amount.toFixed(2)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  }

                  return (
                    <div className='rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 space-y-2'>
                      <p className='text-xs font-bold text-amber-800 uppercase tracking-wide'>
                        ⚠️ Unawarded Money — ${totalUnawarded.toFixed(2)}
                      </p>
                      <p className='text-xs text-amber-700'>
                        No one bet on the winner(s) in the following pool(s).
                        This money was not paid out:
                      </p>
                      <ul className='text-xs text-amber-800 font-semibold space-y-0.5'>
                        {unawardedWin > 0 && (
                          <li>
                            Win Pool: ${unawardedWin.toFixed(2)} (nobody picked
                            1st place)
                          </li>
                        )}
                        {unawardedPlace > 0 && (
                          <li>
                            Place Pool: ${unawardedPlace.toFixed(2)} (nobody
                            picked 1st or 2nd)
                          </li>
                        )}
                        {unawardedShow > 0 && (
                          <li>
                            Show Pool: ${unawardedShow.toFixed(2)} (nobody
                            picked 1st, 2nd, or 3rd)
                          </li>
                        )}
                      </ul>
                      {isOwner && !pm.reconciliation && (
                        <div className='pt-1 space-y-1.5 border-t border-amber-200 mt-1'>
                          <p className='text-xs text-amber-700 font-semibold pt-1'>
                            Blind Draw
                          </p>
                          <p className='text-xs text-amber-700'>
                            Randomly award these funds to{" "}
                            <strong>{drawCount}</strong> bettor
                            {drawCount !== 1 ? "s" : ""} with an equal split.
                            The draw is permanent and cannot be undone or
                            re-run.
                          </p>
                          <div className='flex items-center gap-2'>
                            <label className='text-xs text-amber-700 font-semibold whitespace-nowrap'>
                              # of Winners
                            </label>
                            <input
                              type='number'
                              min={1}
                              max={99}
                              value={drawCount}
                              onChange={(e) =>
                                setDrawCount(
                                  Math.max(1, parseInt(e.target.value) || 1),
                                )
                              }
                              className='w-16 border border-amber-300 rounded-lg px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white'
                            />
                          </div>
                          {reconcileError && (
                            <p className='text-xs text-red-600'>
                              {reconcileError}
                            </p>
                          )}
                          <button
                            onClick={handleReconcile}
                            disabled={reconcileLoading}
                            className='mt-1 w-full py-2 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold transition-colors disabled:opacity-50'
                          >
                            {reconcileLoading
                              ? "Drawing…"
                              : `🎲 Draw ${drawCount} Winner${drawCount !== 1 ? "s" : ""}`}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {(() => {
                  const byBettor = new Map<
                    string,
                    { win: number; place: number; show: number; total: number }
                  >();
                  for (const row of pm.payouts.payoutRows) {
                    if (row.scratched) continue;
                    const cur = byBettor.get(row.bettorName) ?? {
                      win: 0,
                      place: 0,
                      show: 0,
                      total: 0,
                    };
                    cur.win += row.winPayout;
                    cur.place += row.placePayout;
                    cur.show += row.showPayout;
                    cur.total += row.totalPayout;
                    byBettor.set(row.bettorName, cur);
                  }
                  const sorted = Array.from(byBettor.entries())
                    .filter(([, t]) => t.total > 0)
                    .sort((a, b) => b[1].total - a[1].total);

                  // Per-bettor detail rows for expand toggle
                  const rowsByBettor = new Map<string, PariMutuelPayoutRow[]>();
                  for (const row of pm.payouts.payoutRows) {
                    if (row.scratched || row.totalPayout === 0) continue;
                    const rows = rowsByBettor.get(row.bettorName) ?? [];
                    rows.push(row);
                    rowsByBettor.set(row.bettorName, rows);
                  }

                  // Append blind draw winners as extra rows
                  const blindDrawRows: {
                    bettorName: string;
                    amount: number;
                  }[] = pm.reconciliation?.winners ?? [];

                  return (
                    <div className='overflow-x-auto rounded-2xl bg-white shadow-sm border border-gray-100'>
                      <div className='px-3 py-2 bg-green-600 rounded-t-2xl'>
                        <p className='text-xs font-bold text-white uppercase tracking-wide'>
                          Winners
                        </p>
                      </div>
                      {sorted.length === 0 && blindDrawRows.length === 0 ? (
                        <p className='px-3 py-4 text-xs text-gray-400 text-center'>
                          No winning bets.
                        </p>
                      ) : (
                        <table className='w-full text-xs'>
                          <thead className='bg-green-50'>
                            <tr className='text-gray-500'>
                              <th className='text-left px-3 py-2'>Name</th>
                              <th className='text-right px-2 py-2'>Win</th>
                              <th className='text-right px-2 py-2'>Place</th>
                              <th className='text-right px-2 py-2'>Show</th>
                              <th className='text-right px-3 py-2 font-bold'>
                                Total
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {sorted.map(([name, totals]) => {
                              const isExpanded = expandedWinners.has(name);
                              const detailRows = rowsByBettor.get(name) ?? [];
                              return (
                                <React.Fragment key={name}>
                                  <tr
                                    className='border-b border-gray-50 cursor-pointer hover:bg-green-50'
                                    onClick={() =>
                                      setExpandedWinners((prev) => {
                                        const next = new Set(prev);
                                        if (next.has(name)) next.delete(name);
                                        else next.add(name);
                                        return next;
                                      })
                                    }
                                  >
                                    <td className='px-3 py-2 font-semibold text-primary-800'>
                                      <span className='inline-flex items-center gap-1.5'>
                                        <span className='text-gray-400 text-[10px] w-3'>
                                          {isExpanded ? "▲" : "▼"}
                                        </span>
                                        {name}
                                      </span>
                                    </td>
                                    <td className='px-2 py-2 text-right'>
                                      {totals.win > 0
                                        ? `$${totals.win.toFixed(2)}`
                                        : "—"}
                                    </td>
                                    <td className='px-2 py-2 text-right'>
                                      {totals.place > 0
                                        ? `$${totals.place.toFixed(2)}`
                                        : "—"}
                                    </td>
                                    <td className='px-2 py-2 text-right'>
                                      {totals.show > 0
                                        ? `$${totals.show.toFixed(2)}`
                                        : "—"}
                                    </td>
                                    <td className='px-3 py-2 text-right font-bold text-green-700'>
                                      ${totals.total.toFixed(2)}
                                    </td>
                                  </tr>
                                  {isExpanded &&
                                    detailRows.map((row, i) => (
                                      <tr
                                        key={i}
                                        className='bg-green-50 border-b border-green-100'
                                      >
                                        <td className='pl-7 pr-2 py-1.5 text-[11px] text-gray-600 font-medium'>
                                          {row.participantName}
                                        </td>
                                        <td className='px-2 py-1.5 text-right text-[11px] text-gray-500'>
                                          {row.winPayout > 0
                                            ? `$${row.winPayout.toFixed(2)}`
                                            : "—"}
                                        </td>
                                        <td className='px-2 py-1.5 text-right text-[11px] text-gray-500'>
                                          {row.placePayout > 0
                                            ? `$${row.placePayout.toFixed(2)}`
                                            : "—"}
                                        </td>
                                        <td className='px-2 py-1.5 text-right text-[11px] text-gray-500'>
                                          {row.showPayout > 0
                                            ? `$${row.showPayout.toFixed(2)}`
                                            : "—"}
                                        </td>
                                        <td className='px-3 py-1.5 text-right text-[11px] font-semibold text-green-700'>
                                          ${row.totalPayout.toFixed(2)}
                                        </td>
                                      </tr>
                                    ))}
                                </React.Fragment>
                              );
                            })}
                            {blindDrawRows.map((w) => (
                              <tr
                                key={`blind-${w.bettorName}`}
                                className='border-b border-gray-50 bg-purple-50'
                              >
                                <td className='px-3 py-2 font-semibold text-primary-800'>
                                  {w.bettorName}
                                  <span className='ml-1.5 text-purple-600 font-normal'>
                                    🎲 Blind Draw
                                  </span>
                                </td>
                                <td className='px-2 py-2 text-right text-gray-400'>
                                  —
                                </td>
                                <td className='px-2 py-2 text-right text-gray-400'>
                                  —
                                </td>
                                <td className='px-2 py-2 text-right text-gray-400'>
                                  —
                                </td>
                                <td className='px-3 py-2 text-right font-bold text-green-700'>
                                  ${w.amount.toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  );
                })()}

                {(() => {
                  const refundRows = pm.payouts.payoutRows.filter(
                    (r: PariMutuelPayoutRow) => r.scratched,
                  );
                  if (refundRows.length === 0) return null;
                  const byBettor = new Map<string, number>();
                  for (const row of refundRows) {
                    byBettor.set(
                      row.bettorName,
                      (byBettor.get(row.bettorName) ?? 0) + row.totalPayout,
                    );
                  }
                  const sorted = Array.from(byBettor.entries()).sort(
                    (a, b) => b[1] - a[1],
                  );
                  return (
                    <div className='overflow-x-auto rounded-2xl bg-white shadow-sm border border-gray-100'>
                      <div className='px-3 py-2 bg-red-500 rounded-t-2xl'>
                        <p className='text-xs font-bold text-white uppercase tracking-wide'>
                          Refunds
                        </p>
                      </div>
                      <table className='w-full text-xs'>
                        <thead className='bg-red-50'>
                          <tr className='text-gray-500'>
                            <th className='text-left px-3 py-2'>Name</th>
                            <th className='text-right px-3 py-2 font-bold'>
                              Refund
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {sorted.map(([name, total]) => (
                            <tr key={name} className='border-b border-gray-50'>
                              <td className='px-3 py-2 font-semibold text-primary-800'>
                                {name}
                              </td>
                              <td className='px-3 py-2 text-right font-bold text-red-600'>
                                ↩ ${total.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}

                <div className='rounded-2xl bg-white shadow-sm border border-gray-100'>
                  <button
                    className='w-full px-3 py-2 bg-primary-800 rounded-t-2xl flex items-center justify-between'
                    onClick={() => setShowFullBreakdown((v) => !v)}
                  >
                    <p className='text-xs font-bold text-white uppercase tracking-wide'>
                      Full Breakdown
                    </p>
                    <div className='flex items-center gap-2'>
                      {pm.payouts.payoutRows.some(
                        (r: PariMutuelPayoutRow) => r.scratched,
                      ) && (
                        <span className='text-xs text-red-300 font-medium'>
                          ↩ = scratch refund
                        </span>
                      )}
                      <span className='text-white text-xs'>
                        {showFullBreakdown ? "▲" : "▼"}
                      </span>
                    </div>
                  </button>
                  {showFullBreakdown && (
                    <table className='w-full text-xs overflow-x-auto'>
                      <thead className='bg-primary-50'>
                        <tr className='text-gray-500'>
                          <th className='text-left px-3 py-2'>Name</th>
                          <th className='text-left px-2 py-2'>Participant</th>
                          <th className='text-right px-2 py-2'>Win</th>
                          <th className='text-right px-2 py-2'>Place</th>
                          <th className='text-right px-2 py-2'>Show</th>
                          <th className='text-right px-3 py-2 font-bold'>
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {pm.payouts.payoutRows.map(
                          (row: PariMutuelPayoutRow, i: number) => (
                            <tr
                              key={i}
                              className={`border-b border-gray-50 ${
                                row.scratched
                                  ? "bg-red-50"
                                  : row.totalPayout > 0
                                    ? "bg-green-50"
                                    : ""
                              }`}
                            >
                              <td className='px-3 py-2 font-medium'>
                                {row.bettorName}
                              </td>
                              <td className='px-2 py-2'>
                                {row.scratched ? (
                                  <span className='line-through text-red-400'>
                                    {row.participantName ??
                                      `#${row.horseNumber}${row.horseName ? ` ${row.horseName}` : ""}`}
                                  </span>
                                ) : (
                                  <span className='text-gray-600'>
                                    {row.participantName ??
                                      `#${row.horseNumber}${row.horseName ? ` ${row.horseName}` : ""}`}
                                  </span>
                                )}
                              </td>
                              <td className='px-2 py-2 text-right'>
                                {row.scratched
                                  ? row.winBet > 0
                                    ? `$${row.winBet.toFixed(2)}`
                                    : "—"
                                  : row.winPayout > 0
                                    ? `$${row.winPayout.toFixed(2)}`
                                    : "—"}
                              </td>
                              <td className='px-2 py-2 text-right'>
                                {row.scratched
                                  ? row.placeBet > 0
                                    ? `$${row.placeBet.toFixed(2)}`
                                    : "—"
                                  : row.placePayout > 0
                                    ? `$${row.placePayout.toFixed(2)}`
                                    : "—"}
                              </td>
                              <td className='px-2 py-2 text-right'>
                                {row.scratched
                                  ? row.showBet > 0
                                    ? `$${row.showBet.toFixed(2)}`
                                    : "—"
                                  : row.showPayout > 0
                                    ? `$${row.showPayout.toFixed(2)}`
                                    : "—"}
                              </td>
                              <td
                                className={`px-3 py-2 text-right font-bold ${
                                  row.scratched
                                    ? "text-red-600"
                                    : row.totalPayout > 0
                                      ? "text-green-700"
                                      : "text-gray-400"
                                }`}
                              >
                                {row.scratched
                                  ? `↩ $${row.totalPayout.toFixed(2)}`
                                  : row.totalPayout > 0
                                    ? `$${row.totalPayout.toFixed(2)}`
                                    : "$0"}
                              </td>
                            </tr>
                          ),
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
